// src/ConferenceRegistration.jsx
import React, { useEffect, useState, useRef } from "react";
//import "../index.css";
import "../ConferenceRegistration.css";
import TermsAndConditions from "./TermsAndConditions";
import {
  AbstractIntentSelector,
  AbstractSubmissionFields,
  PaperLockedNotice,
  PaymentCompletedNotice,
} from "./AbstractSubmissionModule";
import {
  buildPaymentBreakdown,
  calculatePaymentAmountSummary,
  getPaymentAmount,
  getSuccessfulPayments,
  hasSuccessfulPaymentRecord,
} from "./paymentCalculations";
import {
  confirmRegistration,
  createPaymentOrder,
  deleteCoAuthorsByPaperId,
  fetchPaperCount,
  fetchRegistrationById,
  fetchUserRegistrations,
  saveCoAuthor,
  saveConferenceRegistration,
  savePaperSubmission,
  savePaymentWithBreakdown,
  uploadRegistrationFile,
} from "./conferenceRegistrationApi";
import {
  getActiveRegistrationSteps,
  getNextStepAfterAddress,
  getProgressDisplayStep,
  isAbstractModuleEnabled,
  isInlineAbstractStepEnabled,
  isPostPaymentAbstractAvailable,
  isPostPaymentAbstractEnabled,
  POST_PAYMENT_ABSTRACT_FORM_STEP,
  shouldSkipInlineAbstractStep,
} from "./registrationModules";
import {
  validateBasicSaveContact,
  validateRegistrationStep,
} from "./registrationValidation";
import { buildAdditionalFeesForSave } from "./registrationPayloadBuilders";
import {
  buildPaperFileUploadPayload,
  buildStudentIdCardUploadPayload,
} from "./fileUploadPayloads";
import {
  buildPaperSubmissionPayload,
  hasRequiredPaperSubmissionData,
  resolvePaperStatus,
  shouldSavePaperSubmission,
} from "./paperSubmissionPayloads";
import { savePaperCoAuthors } from "./coAuthorPersistence";
import {
  getReloadedFeeCollections,
  mergeAccompanyingPersonsWithReloadedFees,
} from "./registrationSync";
import {
  ensureRegistrationForFailedPayment,
  ensureRegistrationForPaymentHandler,
  saveRegistrationBeforePayment,
} from "./paymentRegistration";
import {
  buildPaymentOrderPayload,
  createValidatedPaymentOrder,
  logPaymentBreakdownSummary,
} from "./paymentOrder";
import { buildRazorpayOptions, loadRazorpayScript } from "./razorpayCheckout";
import { saveSuccessfulPaymentWithBreakdown } from "./paymentResultPersistence";
import { handlePaymentConfirmationResult } from "./paymentConfirmation";
import { saveFailedPayment } from "./failedPaymentPersistence";
import { useAccompanyingPersons } from "./hooks/useAccompanyingPersons";
import { useCategoryHistory } from "./hooks/useCategoryHistory";
import { useCoAuthors } from "./hooks/useCoAuthors";
import { useExistingRegistrationCheck } from "./hooks/useExistingRegistrationCheck";
import { useExistingRegistrationSync } from "./hooks/useExistingRegistrationSync";
import { useFocusedSessions } from "./hooks/useFocusedSessions";
import { usePaperMetadata } from "./hooks/usePaperMetadata";
import { usePaperSubmissionState } from "./hooks/usePaperSubmissionState";
import { useRegistrationContactValidation } from "./hooks/useRegistrationContactValidation";
import { useRegistrationFees } from "./hooks/useRegistrationFees";
import { useRegistrationLookups } from "./hooks/useRegistrationLookups";
import { useRegistrationNavigation } from "./hooks/useRegistrationNavigation";
import { useStudentIdCardUpload } from "./hooks/useStudentIdCardUpload";
import { useTermsAgreement } from "./hooks/useTermsAgreement";
import {
  AccompanyingPersonsSummarySection,
  AddressSummarySection,
  PersonalSummarySection,
  PaymentStatusSummarySection,
  PaidFocusedSessionsSummarySection,
  ProfessionalSummarySection,
  RegistrationDetailsSummarySection,
  RegistrationFeeSummarySection,
  SummaryTermsAndActionsSection,
  TotalAmountSummarySection,
  ZeroPaymentsSummarySection,
} from "./summarySections";
import {
  AddressStep,
  AccompanyingPersonsInputSection,
  CategoryRegistrationSelectionSection,
  DepartmentSelectionSection,
  FocusedSessionsInputSection,
  InlineAbstractSubmissionStep,
  PaperSubmissionReviewStep,
  PersonalInfoStep,
  ProfessionalInfoStep,
  RegistrationDocumentFieldsSection,
  RegistrationRequirementNotice,
  TrackSelectionSection,
} from "./registrationSteps";

const currentHost = window.location.host;
const TOTAL_STEPS = POST_PAYMENT_ABSTRACT_FORM_STEP;
const MOBILE_MAX_LENGTH = 15;

// Read keys and URLs from .env
const RAZORPAY_KEY_ID = process.env.REACT_APP_RAZORPAY_KEY;
const REGISTRATION_BASE_URL =
  process.env.REACT_APP_REGISTRATION_API_URL || "http://localhost:8085";
const PAYMENT_BASE_URL =
  process.env.REACT_APP_PAYMENT_API_URL || "http://localhost:8085";
const CONFERENCE_API =
  process.env.REACT_APP_CONFERENCE_API_URL ||
  "http://localhost:8080/conference";
const REVIEW_API =
  process.env.REACT_APP_REVIEW_API_URL || "http://localhost:8082";
const USER_API_URL =
  process.env.REACT_APP_USER_API_URL || "http://localhost:8085/registration";

// Frontend-only mapping for additionalData JSON payload.
// Add new entries here to persist more form fields into additionalData
// without any backend entity/controller changes.
const ADDITIONAL_DATA_FIELD_MAPPINGS = [
  { additionalKey: "dietaryRestrictions", formKey: "dietaryRestrictions" },
  { additionalKey: "specialization", formKey: "specialization" },
  { additionalKey: "designation", formKey: "designation" },
  { additionalKey: "selectedTracks", formKey: "selectedTracks" },
];

// Log environment variables for debugging
console.log("🔧 Environment Variables:");
console.log("  REGISTRATION_BASE_URL:", REGISTRATION_BASE_URL);
console.log("  PAYMENT_BASE_URL:", PAYMENT_BASE_URL);
console.log("  CONFERENCE_API:", CONFERENCE_API);

// Updated to use v2 API endpoints
const CONFERENCE_REGISTRATION_URL = `${REGISTRATION_BASE_URL}/registration`;
const CHECK_REGISTRATION_URL = `${REGISTRATION_BASE_URL}/registration/check-registration`;
const ORDER_URL = `${PAYMENT_BASE_URL}/payment/create-order`;
const PAYMENT_VERIFY_URL = `${PAYMENT_BASE_URL}/payment/verify`;
const PAYMENT_SAVE_URL = `${PAYMENT_BASE_URL}/payment/save-with-breakdown`; // Updated to use breakdown endpoint

const ProgressBar = ({ step, abstractStepNumber, hidePostSummarySteps }) => {
  const steps = getActiveRegistrationSteps(abstractStepNumber).filter(
    (flowStep) => !hidePostSummarySteps || flowStep.step <= 6,
  );
  const displayStep = getProgressDisplayStep(step, abstractStepNumber);

  return (
    <div className="progress-bar">
      {steps.map(({ label, step: flowStep }, i) => {
        const status =
          displayStep === flowStep
            ? "current"
            : displayStep > flowStep
              ? "done"
              : "upcoming";
        return (
          <div key={i} className={`progress-step ${status}`} data-label={label}>
            <div className="progress-circle">{flowStep}</div>
            {/* <div className="progress-label">{label}</div> */}
          </div>
        );
      })}
    </div>
  );
};

const ConferenceRegistration = ({
  existingUser,
  prefilledEmail,
  onBackToLogin,
}) => {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentInitiated, setPaymentInitiated] = useState(false);
  const [paymentErrorShown, setPaymentErrorShown] = useState(false);
  const [isExistingUser, setIsExistingUser] = useState(!!existingUser);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [abstractMetadataLoaded, setAbstractMetadataLoaded] = useState(false);
  const [navigationDecisionVersion, setNavigationDecisionVersion] = useState(0);
  const [resumePostPaymentAbstract, setResumePostPaymentAbstract] =
    useState(false);

  // Conveyance fee state (percentage from API)
  const [conveyanceFeePercentage, setConveyanceFeePercentage] = useState(null);

  const {
    abstractStepNumber,
    setAbstractStepNumber,
    abstractConfig,
    setAbstractConfig,
    paperFile,
    setPaperFile,
    paperFileError,
    setPaperFileError,
    abstractText,
    setAbstractText,
    paperTitle,
    setPaperTitle,
    selectedTrack,
    setSelectedTrack,
    selectedTrackName,
    presentationMode,
    setPresentationMode,
    submissionMethod,
    setSubmissionMethod,
    tracks,
    setTracks,
    maxAbstractChars,
    setMaxAbstractChars,
    maxFileSizeMb,
    setMaxFileSizeMb,
    wantsToSubmitAbstract,
    setWantsToSubmitAbstract,
    isPaperLocked,
    setIsPaperLocked,
    paperFileUploaded,
    setPaperFileUploaded,
    paperFileName,
    setPaperFileName,
    submittedPapers,
    setSubmittedPapers,
    existingPaperId,
    setExistingPaperId,
    hasPaperSubmitted,
    setHasPaperSubmitted,
    paperApprovalStatus,
    setPaperApprovalStatus,
    paperSubmissionAction,
    setPaperSubmissionAction,
    showPaperSubmissionSummary,
    setShowPaperSubmissionSummary,
    handleUploadedPaperFileDelete,
    handleSelectedPaperFileRemove,
    handleInlineAbstractTextChange,
    resetPaperFormForNewSubmission,
  } = usePaperSubmissionState();
  const isApprovalMandateEnabled = abstractConfig?.approvalMandate === true;
  const {
    coAuthors,
    setCoAuthors,
    coAuthorErrors,
    handleAddCoAuthor,
    handleAddValidatedCoAuthor,
    handleUpdateCoAuthor,
    handleUpdateValidatedCoAuthor,
    handleRemoveCoAuthor,
    handleRemoveValidatedCoAuthor,
  } = useCoAuthors();

  // Track if registration has been updated (for payment validation)
  const [registrationJustUpdated, setRegistrationJustUpdated] = useState(false);
  const {
    accompanyingPersons,
    setAccompanyingPersons,
    accompanyingPersonFee,
    setAccompanyingPersonFee,
    handleAddAccompanyingPerson,
    handleRemoveAccompanyingPerson,
    handleAccompanyingPersonNameChange,
  } = useAccompanyingPersons({ setRegistrationJustUpdated });

  // Ref to track Razorpay instance and prevent duplicate error handling
  const razorpayInstanceRef = useRef(null);
  const paymentFailureHandledRef = useRef(false);
  const razorpayOpenRef = useRef(false);

  // Ref to track if checkExistingRegistration has loaded conference data
  const conferenceDataLoadedRef = useRef(false);

  // Refs to store navigation decision data (for handling race condition with abstractStepNumber)
  const navigationDecisionRef = useRef({
    isPaid: false,
    hasPaperSubmitted: false,
    paperStatus: null,
    ready: false,
  });
  const registrationCheckRequestIdRef = useRef(0);

  const isPendingPaperApprovalStatus = (status) => {
    const normalized = (status || "").trim().toUpperCase();
    if (!normalized) return false;

    return ["SUBMITTED", "UNDER_REVIEW", "REVIEWED", "PENDING"].includes(
      normalized,
    );
  };

  // Computed: Is abstract/paper submission required? Based on abstractStepNumber
  // abstractStepNumber: 0 = not required/disabled, 3 = required at step 3, 7 = required at step 7
  const isAbstractRequired = isAbstractModuleEnabled(abstractStepNumber);

  // Helper function to render category/registration type history as pills/badges
  const renderHistoryPills = (historyArray, label) => {
    if (!historyArray || historyArray.length === 0) {
      return null;
    }

    const colors = [
      "#007bff",
      "#28a745",
      "#fd7e14",
      "#dc3545",
      "#6f42c1",
      "#17a2b8",
    ];

    return (
      <div
        className="grid-item grid-item--full"
        style={{ marginBottom: "15px" }}
      >
        <div
          style={{
            padding: "12px 16px",
            backgroundColor: "#f8f9fa",
            border: "1px solid #dee2e6",
            borderRadius: "8px",
          }}
        >
          <div
            style={{
              marginBottom: "10px",
              fontSize: "14px",
              fontWeight: "500",
              color: "#495057",
            }}
          >
            Previously Selected {label}:
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            {historyArray.map((item, index) => (
              <div
                key={index}
                style={{
                  display: "inline-block",
                  padding: "6px 12px",
                  backgroundColor: colors[index % colors.length],
                  color: "#fff",
                  borderRadius: "20px",
                  fontSize: "13px",
                  fontWeight: "500",
                  whiteSpace: "nowrap",
                }}
              >
                {item.value}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Initialize form data with existing user data or prefilled email
  const initializeFormData = () => {
    if (existingUser) {
      return {
        id: existingUser.id || "",
        title: existingUser.title || "",
        firstName: existingUser.firstName || "",
        lastName: existingUser.lastName || "",
        email: existingUser.email || "",
        mobile: existingUser.phone || "",
        gender: existingUser.gender || "",
        country: existingUser.country || "",
        state: existingUser.state || "",
        city: existingUser.city || "",
        pincode: existingUser.zipCode || "",
        address: existingUser.address || "",
        affiliation: existingUser.affiliation || "",
        institution: existingUser.institution || "",
        specialization: "",
        designation: "",
        fullNameCertificate: existingUser.getFullName
          ? existingUser.getFullName()
          : `${existingUser.firstName || ""} ${
              existingUser.lastName || ""
            }`.trim(),
        mciRegistered: "No",
        mciNumber: "",
        department: "",
        departmentId: null,
        otherDepartment: "",
        category: "",
        categoryId: null,
        registrationType: "",
        registrationTypeId: null,
        membershipNumber: "",
        isaMembershipNumber: "",
        studentIdCard: null,
        studentIdCardName: "",
        studentIdCardType: "",
        prefix: "",
        conference: "",
      };
    } else {
      return {
        id: "",
        title: "",
        firstName: "",
        lastName: "",
        email: prefilledEmail || "",
        mobile: "",
        gender: "",
        country: "",
        state: "",
        city: "",
        pincode: "",
        address: "",
        affiliation: "",
        institution: "",
        specialization: "",
        designation: "",
        fullNameCertificate: "",
        mciRegistered: "No",
        mciNumber: "",
        department: "",
        departmentId: null,
        otherDepartment: "",
        category: "",
        categoryId: null,
        registrationType: "",
        registrationTypeId: null,
        membershipNumber: "",
        isaMembershipNumber: "",
        studentIdCard: null,
        studentIdCardName: "",
        studentIdCardType: "",
        prefix: "",
        conference: "",
      };
    }
  };

  const [formData, setFormData] = useState(initializeFormData());

  // Handle Track Checkbox
  const trackOptions = [
    "Head and Neck",
    "Breast",
    "GI",
    "Orthopedic Cancer",
    "Thoracic & Lung",
    "GenitoUrinary",
    "Gynaecology",
    "Others",
  ];

  const [checkedTrackItems, setTrackCheckedItems] = useState([]);

  const handleTrackChange = (e) => {
    const { value, checked } = e.target;

    const updated = checked
      ? [...checkedTrackItems, value]
      : checkedTrackItems.filter((item) => item !== value);

    setTrackCheckedItems(updated);

    setFormData((prev) => ({
      ...prev,
      selectedTracks: updated.join(", "),
    }));
  };

  // Reset tracks when category changes
  useEffect(() => {
    setTrackCheckedItems([]);

    setFormData((prev) => ({
      ...prev,
      selectedTracks: "",
    }));
  }, [formData.category]);

  const {
    agreedToTerms,
    showTermsModal,
    setShowTermsModal,
    termsError,
    setTermsError,
    hasReadTerms,
    setHasReadTerms,
    pendingProceedToPayment,
    setPendingProceedToPayment,
    handleTermsAgreementChange,
    handleTermsOpen,
  } = useTermsAgreement();
  const { errors, setErrors, validateField, validateAllMandatoryFields } =
    useRegistrationContactValidation({ formData });
  const {
    studentIdCardFile,
    setStudentIdCardFile,
    studentIdCardError,
    setStudentIdCardError,
    handleStudentIdCardUpload,
  } = useStudentIdCardUpload({ setFormData });
  const {
    savedMembershipNumber,
    setSavedMembershipNumber,
    savedStudentIdCard,
    setSavedStudentIdCard,
    savedStudentIdCardName,
    setSavedStudentIdCardName,
    savedStudentIdCardType,
    setSavedStudentIdCardType,
    setCategoriesHistory,
    setRegistrationTypesHistory,
    initialPaidCategoryId,
    setInitialPaidCategoryId,
    initialPaidRegistrationTypeId,
    setInitialPaidRegistrationTypeId,
    setInitialPaidCategoryValue,
    setInitialPaidRegistrationTypeValue,
    isCurrentRegistrationPaid,
    hasPostPaymentCategoryOrTypeChange,
    previousCategoriesHistory,
    previousRegistrationTypesHistory,
    effectiveCategoriesHistory,
    effectiveRegistrationTypesHistory,
  } = useCategoryHistory({ formData });
  const {
    bannerUrl,
    categories,
    registrationTypes,
    departments,
    countries,
    conferenceId,
    conferenceName,
    focusedSessions,
    focusedSessionAvailability,
    genderOptions,
    fetchFocusedSessions,
  } = useRegistrationLookups({
    conferenceApi: CONFERENCE_API,
    currentHost,
    setFormData,
  });
  usePaperMetadata({
    conferenceApi: CONFERENCE_API,
    conferenceId,
    paid: formData.paid,
    step,
    hasPaperSubmitted,
    setAbstractConfig,
    setAbstractStepNumber,
    setMaxAbstractChars,
    setMaxFileSizeMb,
    setPresentationMode,
    setTracks,
    setAbstractMetadataLoaded,
  });
  const {
    registerForFocusedSessions,
    setRegisterForFocusedSessions,
    selectedFocusedSessions,
    setSelectedFocusedSessions,
    focusedSessionFees,
    setFocusedSessionFees,
    fetchFocusedSessionFee,
    handleFocusedSessionsChange,
    handleFocusedSessionRadioChange,
  } = useFocusedSessions({
    conferenceApi: CONFERENCE_API,
    conferenceId,
    focusedSessions,
    focusedSessionAvailability,
    fetchFocusedSessions,
    additionalFees: formData.additionalFees,
    accompanyingPersonFee,
    setRegistrationJustUpdated,
  });
  const {
    registrationFee,
    feeLoading,
    feeError,
    shouldApplyPaperDiscount,
    currentPaperSequence,
    paperDiscountPercentage,
    calculateOutstandingAmounts,
  } = useRegistrationFees({
    conferenceApi: CONFERENCE_API,
    registrationBaseUrl: REGISTRATION_BASE_URL,
    conferenceId,
    conferenceName,
    formData,
    abstractStepNumber,
    wantsToSubmitAbstract,
    existingPaperId,
    selectedFocusedSessions,
    accompanyingPersons,
    accompanyingPersonFee,
    setAccompanyingPersonFee,
    focusedSessionFees,
    conveyanceFeePercentage,
    hasPostPaymentCategoryOrTypeChange,
    setConveyanceFeePercentage,
  });
  useExistingRegistrationSync({
    existingUser,
    prefilledEmail,
    formData,
    setFormData,
    setIsExistingUser,
    conferenceDataLoadedRef,
    setSavedMembershipNumber,
    setSavedStudentIdCard,
    setSavedStudentIdCardName,
    setSavedStudentIdCardType,
  });
  const { checkExistingRegistration } = useExistingRegistrationCheck({
    existingUser,
    conferenceName,
    registrationBaseUrl: REGISTRATION_BASE_URL,
    conferenceId,
    accompanyingPersonFee,
    setFormData,
    setStep,
    conferenceDataLoadedRef,
    registrationCheckRequestIdRef,
    setHasPaperSubmitted,
    setPaperApprovalStatus,
    setSubmittedPapers,
    setCategoriesHistory,
    setRegistrationTypesHistory,
    setInitialPaidCategoryId,
    setInitialPaidRegistrationTypeId,
    setInitialPaidCategoryValue,
    setInitialPaidRegistrationTypeValue,
    setExistingPaperId,
    setIsPaperLocked,
    setPaperTitle,
    setAbstractText,
    setPresentationMode,
    setSubmissionMethod,
    setSelectedTrack,
    setWantsToSubmitAbstract,
    setPaperFileUploaded,
    setPaperFileName,
    setCoAuthors,
    setAccompanyingPersons,
    setRegisterForFocusedSessions,
    setSelectedFocusedSessions,
    setFocusedSessionFees,
    fetchFocusedSessionFee,
    navigationDecisionRef,
    setShowPaperSubmissionSummary,
    isPendingPaperApprovalStatus,
    isApprovalMandateEnabled,
    abstractStepNumber,
    setResumePostPaymentAbstract,
    setIsInitialLoad,
    setNavigationDecisionVersion,
  });

  // Debug effect to monitor formData.paid changes
  useEffect(() => {}, [formData.paid]);

  useEffect(() => {
    if (!existingUser && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [existingUser, isInitialLoad]);

  // Reset paymentInitiated flag when navigating to/from Step 7
  // This prevents the button from being stuck in "Payment Initiated" state
  useEffect(() => {
    if (step === 7 && paymentInitiated) {
      setPaymentInitiated(false);
    }
  }, [step]);

  useEffect(() => {
    if (
      resumePostPaymentAbstract &&
      isPostPaymentAbstractEnabled(abstractStepNumber)
    ) {
      setShowPaperSubmissionSummary(false);
      setStep(POST_PAYMENT_ABSTRACT_FORM_STEP);
    }
  }, [abstractStepNumber, resumePostPaymentAbstract]);

  // Basic handlers
  const handleChange = (e) => {
    const { name, value } = e.target;
    let nextValue = value;

    if (name === "mobile") {
      nextValue = value.replace(/[^0-9]/g, "");
      if (nextValue.length > MOBILE_MAX_LENGTH) {
        nextValue = nextValue.slice(0, MOBILE_MAX_LENGTH);
      }
    }

    setFormData((p) => ({ ...p, [name]: nextValue }));

    if (["email", "mobile", "firstName", "lastName"].includes(name)) {
      validateField(name, nextValue, {
        value: nextValue,
        country: name === "mobile" ? formData.country : undefined,
      });
    }

    if (name === "category" || name === "registrationType") {
      const newCategory = name === "category" ? nextValue : formData.category;

      const newRegType =
        name === "registrationType" ? nextValue : formData.registrationType;

      const isStudentCategory = newCategory?.toLowerCase().includes("student");

      const isStudentRegType = newRegType?.toLowerCase().includes("student");

      // Student registration if either category OR registration type contains "student"
      const isStudentRegistration = isStudentCategory || isStudentRegType;

      if (isStudentRegistration) {
        console.log("🎓 Student registration detected");
      } else {
        // Neither contains "student"
        setStudentIdCardError("");
      }
    }
  };

  const handleCountryChange = (opt) => {
    const nextCountry = opt ? opt.value : "";
    setFormData((p) => ({ ...p, country: nextCountry }));
    validateField("country", nextCountry);
    if (formData.mobile) {
      validateField("mobile", formData.mobile, {
        country: nextCountry,
        value: formData.mobile,
      });
    }
  };

  // Process additional fees and zero payments when accompanyingPersonFee is loaded
  useEffect(() => {
    // Only process if we have formData with fees AND accompanyingPersonFee is now loaded
    if (
      !accompanyingPersonFee ||
      !formData.additionalFees ||
      formData.additionalFees.length === 0
    ) {
      return;
    }

    // CRITICAL: Don't overwrite user's manual changes
    // Only auto-populate on initial load (when accompanyingPersons is empty)
    // If user has already modified the list, don't overwrite it
    if (accompanyingPersons.length > 0) {
      return; // User has data in state, don't overwrite
    }

    // Check if we've already processed focused sessions
    // Only check selectedFocusedSessions - let accompanying persons be reprocessed if needed
    if (selectedFocusedSessions.length > 0) {
      return; // Already processed
    }

    const storedAdditionalFees = formData.additionalFees || [];
    const storedZeroPayments = formData.zeroPayments || [];

    // Separate accompanying persons from focused sessions
    const accompanyingPersonsFromAdditionalFees = storedAdditionalFees
      .filter((fee) => fee.feeTypeId === accompanyingPersonFee.id)
      .map((fee) => ({
        name: fee.name,
        paymentId: fee.paymentId,
        paid: !!fee.paymentId,
        feeTypeId: fee.feeTypeId,
        id: fee.id,
      }));

    const accompanyingPersonsFromZeroPayments = storedZeroPayments
      .filter((zp) => zp.feeTypeId === accompanyingPersonFee.id)
      .map((zp) => ({
        name: zp.name,
        paymentId: null,
        paid: true,
        feeTypeId: zp.feeTypeId,
        id: zp.id,
      }));

    const accompanyingPersonsList = [
      ...accompanyingPersonsFromAdditionalFees,
      ...accompanyingPersonsFromZeroPayments,
    ];

    if (accompanyingPersonsList.length > 0) {
      setAccompanyingPersons(accompanyingPersonsList);
    }

    // Load focused sessions
    // CRITICAL: Only load UNPAID focused sessions so paid ones don't appear in outstanding
    const focusedSessionsFromAdditionalFees = storedAdditionalFees
      .filter(
        (fee) => fee.feeTypeId !== accompanyingPersonFee.id && !fee.paymentId,
      )
      .map((fee) => fee.feeTypeId);

    const focusedSessionsFromZeroPayments = storedZeroPayments
      .filter((zp) => zp.feeTypeId !== accompanyingPersonFee.id)
      .map((zp) => zp.feeTypeId);

    const focusedSessionsList = [
      ...new Set([
        ...focusedSessionsFromAdditionalFees,
        ...focusedSessionsFromZeroPayments,
      ]),
    ];

    if (focusedSessionsList.length > 0) {
      setRegisterForFocusedSessions("Yes");
      setSelectedFocusedSessions(focusedSessionsList);

      // Fetch fees for these sessions (needed to display rates)
      focusedSessionsList.forEach((sessionId) => {
        fetchFocusedSessionFee(sessionId).then((feeData) => {
          if (feeData) {
            setFocusedSessionFees((prevFees) => ({
              ...prevFees,
              [sessionId]: feeData,
            }));
          }
        });
      });
    }
  }, [accompanyingPersonFee, formData.additionalFees, formData.zeroPayments]); // Run when accompanyingPersonFee loads

  const normalizedPaperApprovalStatus = (
    paperApprovalStatus || "PENDING"
  ).toUpperCase();

  const isRevisionPaperStatus =
    normalizedPaperApprovalStatus === "MINOR_REVISION" ||
    normalizedPaperApprovalStatus === "MAJOR_REVISION" ||
    normalizedPaperApprovalStatus === "REVISION_REQUESTED";
  const isDraftPaperStatus = normalizedPaperApprovalStatus === "DRAFT";
  const hasRejectedPaper = submittedPapers.some(
    (paper) => (paper.status || "").trim().toUpperCase() === "REJECTED",
  );
  const isSubmittedOrLockedPaperStatus = [
    "SUBMITTED",
    "UNDER_REVIEW",
    "ACCEPTED",
    "REJECTED",
    "REVIEWED",
  ].includes(normalizedPaperApprovalStatus);

  const readablePaperApprovalStatus =
    {
      DRAFT: "Draft",
      SUBMITTED: "Submitted",
      UNDER_REVIEW: "Under Review",
      REVIEWED: "Reviewed",
      REVISION_REQUESTED: "Revision Requested",
      ACCEPTED: "Accepted",
      REJECTED: "Rejected",
    }[normalizedPaperApprovalStatus] || normalizedPaperApprovalStatus;

  const paymentBlockedPaperStatusMessage =
    normalizedPaperApprovalStatus === "REJECTED"
      ? `Paper status: ${readablePaperApprovalStatus}. Payment is unavailable because this submission was rejected. Please contact the conference organizers.`
      : normalizedPaperApprovalStatus === "REVISION_REQUESTED"
        ? `Paper status: ${readablePaperApprovalStatus}. Submit the requested revision and wait for acceptance before payment.`
        : isRevisionPaperStatus
          ? `Paper status: ${readablePaperApprovalStatus}. Please revise your paper and resubmit before payment.`
          : `Paper status: ${readablePaperApprovalStatus}. Payment will be enabled after your paper is accepted.`;

  const isPaperAwaitingReview =
    hasPaperSubmitted && isPendingPaperApprovalStatus(paperApprovalStatus);

  // In step-3 mode, payment is blocked only when metadata mandates review approval.
  const isPaymentBlockedPendingPaperApproval =
    isApprovalMandateEnabled &&
    isInlineAbstractStepEnabled(abstractStepNumber) &&
    isPaperAwaitingReview;

  const maxPaperSubmissions = Math.max(
    1,
    Number(abstractConfig?.maxSubmissions) || 1,
  );
  const configuredAbstractStepNumber =
    abstractConfig?.abstractStepNumber || abstractStepNumber;
  const isPostPaymentAbstractMode = isPostPaymentAbstractEnabled(
    configuredAbstractStepNumber,
  );
  const hasCompletedPayment =
    formData.paid === true ||
    formData.paid === "true" ||
    hasSuccessfulPaymentRecord(formData.payment);
  const submittedPaperCount = submittedPapers.length;
  const allSubmittedPapersAccepted =
    submittedPaperCount > 0 &&
    submittedPapers.every(
      (paper) => (paper.status || "").trim().toUpperCase() === "ACCEPTED",
    );
  const canEditCurrentPaperSubmission =
    Boolean(existingPaperId) &&
    !isPaperLocked &&
    (isDraftPaperStatus || isRevisionPaperStatus);
  const canSubmitAnotherPostPaymentPaper =
    (isPostPaymentAbstractMode || canEditCurrentPaperSubmission) &&
    (hasCompletedPayment || canEditCurrentPaperSubmission) &&
    !hasRejectedPaper &&
    (canEditCurrentPaperSubmission ||
      (submittedPaperCount < maxPaperSubmissions &&
        (submittedPaperCount === 0 || allSubmittedPapersAccepted)));
  const shouldRenderPostPaymentAbstractForm =
    isPostPaymentAbstractMode ||
    canEditCurrentPaperSubmission ||
    resumePostPaymentAbstract;
  const canSubmitAnotherInlinePaper =
    isInlineAbstractStepEnabled(abstractStepNumber) &&
    !isPaymentBlockedPendingPaperApproval &&
    !hasRejectedPaper &&
    submittedPaperCount < maxPaperSubmissions &&
    (submittedPaperCount === 0 || allSubmittedPapersAccepted);
  const canShowPaperSubmitActions =
    !isSubmittedOrLockedPaperStatus &&
    !isPaperLocked &&
    (!existingPaperId ||
      canEditCurrentPaperSubmission ||
      canSubmitAnotherPostPaymentPaper);

  useEffect(() => {
    if (
      step === 3 &&
      wantsToSubmitAbstract === true &&
      canSubmitAnotherInlinePaper &&
      isPaperLocked &&
      allSubmittedPapersAccepted
    ) {
      resetPaperFormForNewSubmission();
      setCoAuthors([]);
      setShowPaperSubmissionSummary(false);
    }
  }, [
    allSubmittedPapersAccepted,
    canSubmitAnotherInlinePaper,
    isPaperLocked,
    resetPaperFormForNewSubmission,
    setCoAuthors,
    setShowPaperSubmissionSummary,
    step,
    wantsToSubmitAbstract,
  ]);

  const handleSubmitAnotherPostPaymentPaper = () => {
    setResumePostPaymentAbstract(false);
    resetPaperFormForNewSubmission();
    setCoAuthors([]);
    setWantsToSubmitAbstract(true);
    setStep(POST_PAYMENT_ABSTRACT_FORM_STEP);
  };

  const buildAdditionalDataPayload = () => {
    const additionalData = {};

    // Map configured form fields into additionalData (string fields trimmed).
    ADDITIONAL_DATA_FIELD_MAPPINGS.forEach(({ additionalKey, formKey }) => {
      const value = formData?.[formKey];

      if (typeof value === "string") {
        const trimmedValue = value.trim();
        if (trimmedValue) {
          additionalData[additionalKey] = trimmedValue;
        }
        return;
      }

      if (value !== undefined && value !== null && value !== "") {
        additionalData[additionalKey] = value;
      }
    });

    // Persist multi-select append history in JSON column instead of comma-separated strings
    if (effectiveCategoriesHistory.length > 0) {
      additionalData.categoriesHistory = effectiveCategoriesHistory
        .map((entry) => ({
          id: entry?.id ?? null,
          value: entry?.value || "",
          selectedAt: entry?.selectedAt || new Date().toISOString(),
        }))
        .filter((entry) => entry.value);
    }

    if (effectiveRegistrationTypesHistory.length > 0) {
      additionalData.registrationTypesHistory =
        effectiveRegistrationTypesHistory
          .map((entry) => ({
            id: entry?.id ?? null,
            value: entry?.value || "",
            selectedAt: entry?.selectedAt || new Date().toISOString(),
          }))
          .filter((entry) => entry.value);
    }

    return additionalData;
  };

  const buildConferenceRegistrationPayload = ({
    additionalFees = null,
    includeAccompanyingPersons = false,
    includeGender = false,
    includeIsaMembershipNumber = false,
  } = {}) => {
    const payload = {
      email: formData.email,
      title: formData.title,
      firstName: formData.firstName,
      lastName: formData.lastName,
      phone: formData.mobile,
      country: formData.country,
      state: formData.state,
      city: formData.city,
      address: formData.address,
      zipCode: formData.pincode,
      conference: formData.conference || conferenceName || currentHost,
      affiliation: formData.affiliation || "",
      institution: formData.institution || "",
      prefix: formData.prefix || "",
      fullNameCertificate: formData.fullNameCertificate,
      mciRegistered: formData.mciRegistered || "No",
      mciName: formData.mciName || "",
      mciNumber: formData.mciNumber || "",
      department: formData.department || "",
      departmentId: formData.departmentId,
      otherDepartment: formData.otherDepartment,
      category: formData.category || "TBD",
      categoryId: formData.categoryId,
      registrationType: formData.registrationType || "TBD",
      registrationTypeId: formData.registrationTypeId,
      membershipNumber: formData.membershipNumber || "",
      studentIdURL:
        formData.studentIdCard && !formData.studentIdCard.startsWith("data:")
          ? formData.studentIdCard
          : null,
      specialRequirements: formData.specialRequirements || "",
      accommodationNeeded: formData.accommodationNeeded || false,
      notes: formData.notes || "",
      additionalData: buildAdditionalDataPayload(),
    };

    if (includeGender) {
      payload.gender = formData.gender;
    }

    if (includeIsaMembershipNumber) {
      payload.isaMembershipNumber = formData.isaMembershipNumber || "";
    }

    if (additionalFees !== null) {
      payload.additionalFees = additionalFees;
    }

    if (includeAccompanyingPersons) {
      payload.accompanyingPersons =
        accompanyingPersons.length > 0 ? accompanyingPersons : [];
    }

    return payload;
  };

  // Step navigation with field validation
  const validateCurrentStep = (currentStep) => {
    if (currentStep === 1) {
      const emailValid = validateField("email", formData.email);
      const mobileValid = validateField("mobile", formData.mobile, {
        country: formData.country,
        value: formData.mobile,
      });

      if (!emailValid || !mobileValid) {
        return false;
      }
    }

    const validation = validateRegistrationStep({
      currentStep,
      formData,
      genderOptions,
      abstractStepNumber:
        currentStep === POST_PAYMENT_ABSTRACT_FORM_STEP
          ? configuredAbstractStepNumber
          : abstractStepNumber,
      wantsToSubmitAbstract,
      paperTitle,
      tracks,
      selectedTrack,
      presentationMode,
      submissionMethod,
      abstractText,
      maxAbstractChars,
      paperFile,
      paperFileUploaded,
      categories,
      registrationTypes,
      checkedTrackItems,
      accompanyingPersons,
    });

    if (validation.studentIdCardError !== null) {
      setStudentIdCardError(validation.studentIdCardError);
    }

    if (validation.abstractCharLimitError) {
      alert(validation.abstractCharLimitError);
      return false;
    }

    console.log("📋 Validation complete for step", currentStep);
    console.log("  Required fields missing:", validation.requiredFields.length);
    if (validation.requiredFields.length > 0) {
      console.log("  Missing fields:", validation.requiredFields);
      alert(
        `Please fill in the following required fields: ${validation.requiredFields.join(
          ", ",
        )}`,
      );
      return false;
    }

    console.log("✅ All validations passed for step", currentStep);
    return true;
  };

  const scrollToTermsCheckbox = () => {
    const termsCheckbox = document.querySelector(".terms-checkbox-container");
    if (termsCheckbox) {
      termsCheckbox.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const { next, prev } = useRegistrationNavigation({
    step,
    setStep,
    totalSteps: TOTAL_STEPS,
    abstractStepNumber,
    abstractMetadataLoaded,
    validateCurrentStep,
    formData,
    isPaymentBlockedPendingPaperApproval,
    paymentBlockedPaperStatusMessage,
    agreedToTerms,
    setTermsError,
    scrollToTermsCheckbox,
    showPaperSubmissionSummary,
    setShowPaperSubmissionSummary,
    navigationDecisionRef,
    navigationDecisionVersion,
    conferenceDataLoadedRef,
    isPendingPaperApprovalStatus,
    isApprovalMandateEnabled,
    isPaperNavigationLocked: isPaymentBlockedPendingPaperApproval,
    setIsInitialLoad,
  });

  const handleProceedToPayment = async () => {
    if (isPaymentBlockedPendingPaperApproval) {
      alert(paymentBlockedPaperStatusMessage);
      return;
    }

    // Force users to read terms before they can proceed.
    if (!hasReadTerms) {
      setPendingProceedToPayment(true);
      setTermsError(
        "Please read the Terms & Conditions before proceeding to payment.",
      );
      setShowTermsModal(true);
      scrollToTermsCheckbox();
      return;
    }

    if (!agreedToTerms) {
      setTermsError(
        "You must agree to the Terms & Conditions before proceeding to payment.",
      );
      scrollToTermsCheckbox();
      return;
    }

    const saveSuccess = await saveStep();
    if (saveSuccess) {
      setPendingProceedToPayment(false);
      setStep(7);
    }
  };

  const handleTermsAcknowledged = async () => {
    setHasReadTerms(true);

    if (isPaymentBlockedPendingPaperApproval) {
      setPendingProceedToPayment(false);
      setTermsError(paymentBlockedPaperStatusMessage);
      return;
    }

    // If user clicked "Proceed to Payment" first, continue flow right after reading terms.
    if (!pendingProceedToPayment) {
      return;
    }

    if (!agreedToTerms) {
      setPendingProceedToPayment(false);
      setTermsError(
        "Please check the Terms & Conditions agreement box to proceed to payment.",
      );
      scrollToTermsCheckbox();
      return;
    }

    const saveSuccess = await saveStep();
    if (saveSuccess) {
      setPendingProceedToPayment(false);
      setTermsError("");
      setStep(7);
    }
  };

  // Combined Save & Continue function
  const saveAndContinue = async () => {
    if (!validateCurrentStep(step)) {
      return;
    }

    // Save the registration and get success status
    const saveSuccess = await saveStep();

    // If save was successful, sync registration state from backend before proceeding
    if (saveSuccess) {
      try {
        // Use latest email and conferenceName for fetch
        const emailToFetch = formData.email;
        const confNameToFetch = formData.conference || conferenceName;
        if (emailToFetch && confNameToFetch) {
          // Fetch latest registration from backend
          const registrations = await fetchUserRegistrations({
            registrationBaseUrl: REGISTRATION_BASE_URL,
            email: emailToFetch,
            conferenceName: confNameToFetch,
          });
          if (registrations.length > 0) {
            // Find the registration for this specific conference
            const existingReg = registrations.find(
              (reg) => reg.conference === confNameToFetch && !reg.cancelled,
            );
            if (existingReg) {
              // Determine payment status the same way we do elsewhere
              const syncedIsPaid =
                existingReg.paid === true ||
                hasSuccessfulPaymentRecord(existingReg.payment) ||
                existingReg.confirmationDate !== null;

              // Merge registration but preserve computed/normalized fields
              setFormData((prev) => ({
                ...prev,
                ...existingReg,
                paid: syncedIsPaid,
                additionalFees:
                  existingReg.additionalFees &&
                  Array.isArray(existingReg.additionalFees)
                    ? existingReg.additionalFees
                    : [],
                zeroPayments:
                  existingReg.zeroPayments &&
                  Array.isArray(existingReg.zeroPayments)
                    ? existingReg.zeroPayments
                    : [],
                payment:
                  existingReg.payment && Array.isArray(existingReg.payment)
                    ? existingReg.payment
                    : [],
              }));

              // CRITICAL: Don't overwrite accompanyingPersons state here!
              // The saveStep() function already updated it properly after reloading.
              // If we overwrite here, we'll restore deleted items from the database.
              // The state should remain as the user set it, and saveStep() already synced it.

              // Only sync focused sessions (not accompanying persons)
              const storedAdditionalFees =
                existingReg.additionalFees &&
                Array.isArray(existingReg.additionalFees)
                  ? existingReg.additionalFees
                  : [];
              const storedZeroPayments =
                existingReg.zeroPayments &&
                Array.isArray(existingReg.zeroPayments)
                  ? existingReg.zeroPayments
                  : [];

              // Focused sessions: build array of objects with paid/paymentId
              if (accompanyingPersonFee) {
                // CRITICAL: Only load UNPAID focused sessions so paid ones don't appear in outstanding
                const focusedSessionsFromAdditionalFees = storedAdditionalFees
                  .filter(
                    (fee) =>
                      fee.feeTypeId !== accompanyingPersonFee.id &&
                      !fee.paymentId,
                  )
                  .map((fee) => fee.feeTypeId); // Just the ID, not the whole object
                const focusedSessionsFromZeroPayments = storedZeroPayments
                  .filter((zp) => zp.feeTypeId !== accompanyingPersonFee.id)
                  .map((zp) => zp.feeTypeId); // Just the ID, not the whole object
                // Combine and deduplicate
                const focusedSessionsList = [
                  ...new Set([
                    ...focusedSessionsFromAdditionalFees,
                    ...focusedSessionsFromZeroPayments,
                  ]),
                ];
                setRegisterForFocusedSessions(
                  focusedSessionsList.length > 0 ? "Yes" : "No",
                );
                setSelectedFocusedSessions(focusedSessionsList);
                // Optionally, fetch fees for these sessions if needed
              }
            }
          }
        }
      } catch (err) {
        // If sync fails, proceed anyway
        console.error("Failed to sync registration after save:", err);
      }

      if (
        step === 3 &&
        isInlineAbstractStepEnabled(abstractStepNumber) &&
        wantsToSubmitAbstract === true &&
        isPaymentBlockedPendingPaperApproval
      ) {
        alert(
          "Please wait for your paper approval before proceeding to registration!!!",
        );
        return;
      }

      if (step === 4 && isPaymentBlockedPendingPaperApproval) {
        alert(
          "Your paper is currently under review. You will receive an email once it has been approved. Please log in again to continue with your registration.",
        );
        return;
      }

      if (step === 2) {
        setStep(getNextStepAfterAddress(abstractStepNumber));
        return;
      }

      setStep((s) => Math.min(s + 1, TOTAL_STEPS));
    }
  };

  // Save current step
  const saveStep = async (paperActionOverride = null) => {
    // For partial save, validate basic fields but be flexible with mobile if no country selected
    const contactValidation = validateBasicSaveContact({
      formData,
      validateField,
    });

    if (contactValidation.mobileError !== null) {
      setErrors((prev) => ({ ...prev, mobile: contactValidation.mobileError }));
    }

    if (!contactValidation.isValid) {
      alert("Please correct the highlighted contact details before saving.");
      setStep(1);
      return false; // Return false to indicate validation failure
    }

    try {
      setSaving(true);
      const additionalFeesArray = buildAdditionalFeesForSave({
        formData,
        accompanyingPersons,
        accompanyingPersonFee,
        selectedFocusedSessions,
        focusedSessions,
        focusedSessionFees,
      });

      // CRITICAL: Always send additionalFees array (even if empty) to sync deletions
      // Backend will remove any unpaid items NOT in this list
      const conferenceRegistrationData = buildConferenceRegistrationPayload({
        additionalFees: additionalFeesArray,
        includeGender: true,
        includeIsaMembershipNumber: true,
      });
      console.log("📤 Sending additionalFees to backend (will sync with DB):");
      console.log("   Total items:", additionalFeesArray.length);
      console.log(
        "   Accompanying persons:",
        additionalFeesArray.filter(
          (f) => f.feeTypeId === accompanyingPersonFee?.id,
        ).length,
      );
      console.log(
        "   Focused sessions:",
        additionalFeesArray.filter(
          (f) => f.feeTypeId !== accompanyingPersonFee?.id,
        ).length,
      );

      console.log("👥 Items being sent in additionalFees array:");
      conferenceRegistrationData.additionalFees.forEach((fee, idx) => {
        const itemType =
          fee.feeTypeId === accompanyingPersonFee?.id
            ? "Accompanying Person"
            : "Focused Session";
        console.log(`   [${idx}] ${itemType}: ${fee.name} - rate: ${fee.rate}`);
      });
      console.log(
        "   ⚠️ Backend will DELETE any UNPAID items NOT in this list!",
      );

      // Determine if this is an update or new registration
      const isUpdate = formData.id || formData.regId;
      const method = isUpdate ? "PUT" : "POST";
      // CRITICAL: Use formData.id (registrationId like "isa005"), NOT formData.regId (dbId like 8)
      const registrationIdForUpdate = formData.id || formData.regId;
      const url = isUpdate
        ? `${CONFERENCE_REGISTRATION_URL}/${registrationIdForUpdate}`
        : CONFERENCE_REGISTRATION_URL;

      console.log("💾 Save Registration:");
      console.log("  formData.id (registrationId):", formData.id);
      console.log("  formData.regId (dbId):", formData.regId);
      console.log("  Using registrationIdForUpdate:", registrationIdForUpdate);
      console.log("  Method:", method);
      console.log("  URL:", url);
      console.log("  Is Update?:", isUpdate);
      console.log("  Is New Conference Registration?:", !isUpdate);
      console.log("  Conference:", conferenceRegistrationData.conference);
      console.log("  Category:", conferenceRegistrationData.category);
      console.log("  CategoryId:", conferenceRegistrationData.categoryId);
      console.log(
        "  Registration Type:",
        conferenceRegistrationData.registrationType,
      );
      console.log(
        "  RegistrationTypeId:",
        conferenceRegistrationData.registrationTypeId,
      );
      console.log("═══════════════════════════════════════════");
      console.log("📤 PAYLOAD BEING SENT TO BACKEND:");
      console.log(JSON.stringify(conferenceRegistrationData, null, 2));
      console.log("═══════════════════════════════════════════");

      const saveResult = await saveConferenceRegistration({
        conferenceRegistrationUrl: CONFERENCE_REGISTRATION_URL,
        registrationId: isUpdate ? registrationIdForUpdate : null,
        payload: conferenceRegistrationData,
      });

      if (!saveResult.ok) {
        const txt = saveResult.errorText;
        console.error("❌ Save failed!");
        console.error("  Status:", saveResult.status, saveResult.statusText);
        console.error("  Response:", txt);
        console.error(
          "  Was trying to save for existing user:",
          !!formData.email,
        );
        console.error("  Conference:", conferenceRegistrationData.conference);
        alert(`Save failed: ${txt}`);
        return false; // Return false to indicate save failure
      } else {
        const saved = saveResult.data;
        console.log("✅ Save successful!");
        console.log("  Saved data:", saved);
        console.log("  Registration ID:", saved.registrationId);
        console.log("  DB ID:", saved.dbId || saved.id);

        // Set flag to indicate registration was just updated successfully
        setRegistrationJustUpdated(true);

        // Update registration IDs
        if (saved.registrationId) {
          console.log("  Setting formData.id to:", saved.registrationId);
          setFormData((prev) => ({ ...prev, id: saved.registrationId }));
        }
        if (saved.dbId || saved.id) {
          console.log("  Setting formData.regId to:", saved.dbId || saved.id);
          setFormData((prev) => ({ ...prev, regId: saved.dbId || saved.id }));
        }

        // Upload student ID card file to database if present
        if (
          (saved.dbId || saved.id) &&
          formData.studentIdCard &&
          formData.studentIdCard.startsWith("data:")
        ) {
          try {
            const registrationId = saved.dbId || saved.id;
            console.log(
              "📤 Uploading student ID card to database for registration:",
              registrationId,
            );

            const fileData = buildStudentIdCardUploadPayload({
              studentIdCard: formData.studentIdCard,
              studentIdCardName: formData.studentIdCardName,
              studentIdCardType: formData.studentIdCardType,
            });

            console.log("📤 File data prepared:", {
              fileName: fileData.fileName,
              contentType: fileData.contentType,
              fileSize: fileData.fileSize,
              base64Length: fileData.base64Length,
            });

            console.log(
              "📤 Uploading to:",
              `${REGISTRATION_BASE_URL}/registration/${registrationId}/file/id-card`,
            );

            const fileUploadResult = await uploadRegistrationFile({
              registrationBaseUrl: REGISTRATION_BASE_URL,
              registrationId,
              fileType: "id-card",
              payload: {
                fileType: fileData.fileType,
                fileData: fileData.fileData,
                fileName: fileData.fileName,
                contentType: fileData.contentType,
                fileSize: fileData.fileSize,
              },
            });

            if (!fileUploadResult.ok) {
              const errorText = fileUploadResult.errorText;
              console.error("❌ Failed to upload student ID card:", errorText);
              alert("Warning: File upload failed. Please try uploading again.");
              // Don't fail the entire save, just log it
            } else {
              const result = fileUploadResult.data;
              console.log(
                "✅ Student ID card uploaded successfully to database:",
                result,
              );

              // Clear the base64 data and set a marker that file is in database
              setFormData((prev) => ({
                ...prev,
                studentIdCard: "DATABASE", // Marker to indicate file is stored in database
                studentIdCardUploaded: true,
              }));
            }
          } catch (fileErr) {
            console.error("Error uploading student ID card:", fileErr);
            // Don't fail the entire save, just log it
          }
        }

        // Save paper submission if on step 3 and paper data exists
        console.log("🔍 Checking paper submission conditions:", {
          step,
          isAbstractRequired,
          wantsToSubmitAbstract,
          submissionMethod,
          paperTitle: paperTitle?.trim(),
          selectedTrack,
          abstractText: abstractText?.trim(),
          paperFile: paperFile?.name,
          registrationId: saved.dbId || saved.id,
        });

        const hasRequiredPaperData = hasRequiredPaperSubmissionData({
          paperTitle,
          selectedTrack,
          submissionMethod,
          abstractText,
          paperFile,
        });

        const shouldSavePaper = shouldSavePaperSubmission({
          hasRequiredPaperData,
          step,
          isAbstractRequired,
          wantsToSubmitAbstract,
          abstractStepNumber,
        });

        if (shouldSavePaper) {
          try {
            const registrationId = saved.dbId || saved.id;
            console.log(
              "📄 Starting paper submission for registration:",
              registrationId,
            );

            // First, upload the paper file if present
            if (paperFile) {
              console.log("📤 Uploading paper file:", paperFile.name);

              const fileData = await buildPaperFileUploadPayload(paperFile);

              console.log("📤 File data prepared:", {
                fileName: fileData.fileName,
                contentType: fileData.contentType,
                fileSize: fileData.fileSize,
                base64Length: fileData.base64Length,
              });

              console.log(
                "📤 Uploading to:",
                `${REGISTRATION_BASE_URL}/registration/${registrationId}/file/paper`,
              );

              const fileUploadResult = await uploadRegistrationFile({
                registrationBaseUrl: REGISTRATION_BASE_URL,
                registrationId,
                fileType: "paper",
                payload: {
                  fileType: fileData.fileType,
                  fileData: fileData.fileData,
                  fileName: fileData.fileName,
                  contentType: fileData.contentType,
                  fileSize: fileData.fileSize,
                },
              });

              if (fileUploadResult.ok) {
                const fileResult = fileUploadResult.data;
                console.log("✅ Paper file uploaded successfully:", fileResult);
                // The file is saved in registration_files table, we'll reference it via registrationId and fileType
              } else {
                const errorText = fileUploadResult.errorText;
                console.error("❌ Failed to upload paper file:", errorText);
                throw new Error("Failed to upload paper file");
              }
            }

            const { effectivePaperAction, currentPaperStatus, paperStatus } =
              resolvePaperStatus({
                paperActionOverride,
                paperSubmissionAction,
                paperApprovalStatus,
                submissionMethod,
              });
            console.log(
              `🧭 Paper status transition: ${currentPaperStatus || "NONE"} -> ${paperStatus}`,
            );
            console.log(
              `📝 Paper status will be: ${paperStatus} (action: "${effectivePaperAction}")`,
            );

            const paperData = buildPaperSubmissionPayload({
              registrationId,
              selectedTrack,
              paperTitle,
              abstractText,
              paperStatus,
              presentationMode,
            });

            let savedPaper;
            if (existingPaperId) {
              // Update existing paper
              console.log(
                "📝 Updating existing paper ID:",
                existingPaperId,
                "with data:",
                paperData,
              );
              const paperResult = await savePaperSubmission({
                registrationBaseUrl: REGISTRATION_BASE_URL,
                paperId: existingPaperId,
                payload: paperData,
              });

              if (!paperResult.ok) {
                const errorText = paperResult.errorText;
                console.error("❌ Failed to update paper:", errorText);
                throw new Error(
                  "Failed to update paper submission: " + errorText,
                );
              }

              savedPaper = paperResult.data;
              console.log("✅ Paper updated successfully:", savedPaper);
              setSubmittedPapers((currentPapers) =>
                currentPapers.map((paper) =>
                  String(paper.id) === String(savedPaper.id)
                    ? savedPaper
                    : paper,
                ),
              );
            } else {
              // Create new paper
              console.log("📄 Creating new paper entity:", paperData);
              const paperResult = await savePaperSubmission({
                registrationBaseUrl: REGISTRATION_BASE_URL,
                payload: paperData,
              });

              if (!paperResult.ok) {
                const errorText = paperResult.errorText;
                console.error("❌ Failed to create paper:", errorText);
                throw new Error(
                  "Failed to save paper submission: " + errorText,
                );
              }

              savedPaper = paperResult.data;
              console.log("✅ Paper created successfully:", savedPaper);

              // Store the paper ID for future updates
              setExistingPaperId(savedPaper.id);
              setSubmittedPapers((currentPapers) => [
                ...currentPapers,
                savedPaper,
              ]);
            }

            await savePaperCoAuthors({
              registrationBaseUrl: REGISTRATION_BASE_URL,
              savedPaper,
              existingPaperId,
              coAuthors,
              deleteCoAuthorsByPaperId,
              saveCoAuthor,
            });

            console.log("✅ Paper submission completed successfully");
            setHasPaperSubmitted(true); // Update state to reflect paper has been submitted
            setPaperApprovalStatus(savedPaper?.status || paperStatus);
          } catch (paperErr) {
            console.error("❌ Error saving paper submission:", paperErr);
            alert(
              "Warning: Paper submission failed. " +
                paperErr.message +
                "\nPlease try again from the Paper/Abstract step.",
            );
            // Don't fail the entire save, just alert the user
          }
        } else {
          console.log("⚠️ Paper submission skipped - conditions not met");
        }

        // Reload full registration data to get updated additionalFees
        if (saved.dbId || saved.id) {
          try {
            const reloadedReg = await fetchRegistrationById({
              conferenceRegistrationUrl: CONFERENCE_REGISTRATION_URL,
              registrationId: saved.dbId || saved.id,
            });
            if (reloadedReg) {
              console.log(
                "📥 Reloaded registration data from backend:",
                reloadedReg,
              );
              console.log(
                "   Backend returned additionalFees count:",
                reloadedReg.additionalFees?.length || 0,
              );
              console.log(
                "   Backend returned zeroPayments count:",
                reloadedReg.zeroPayments?.length || 0,
              );

              // Update formData with additionalFees AND zeroPayments so the payment detection works
              const reloadedFees = getReloadedFeeCollections(reloadedReg);
              if (Array.isArray(reloadedReg.additionalFees)) {
                setFormData((prev) => ({
                  ...prev,
                  additionalFees: reloadedFees.additionalFees,
                  zeroPayments: reloadedFees.zeroPayments, // Also update zeroPayments
                }));
                console.log(
                  "✅ Updated formData.additionalFees:",
                  reloadedFees.additionalFees,
                );
                console.log(
                  "✅ Updated formData.zeroPayments:",
                  reloadedFees.zeroPayments,
                );

                // CRITICAL: Only update accompanyingPersons to ADD database IDs
                // Do NOT restore deleted items from database!
                if (accompanyingPersonFee) {
                  const updatedAccompanyingPersons =
                    mergeAccompanyingPersonsWithReloadedFees({
                      accompanyingPersons,
                      accompanyingPersonFee,
                      reloadedRegistration: reloadedReg,
                    });
                  setAccompanyingPersons(updatedAccompanyingPersons);
                }
              }
            }
          } catch (reloadErr) {
            console.error("Error reloading registration:", reloadErr);
            // Don't fail the save operation, just log it
          }
        }

        return true; // Return true to indicate successful save
      }
    } catch (err) {
      console.error("Save error:", err);
      alert(`Could not save. Error: ${err.message}`);
      return false; // Return false to indicate error
    } finally {
      setSaving(false);
    }
  };

  // Detect environment based on Razorpay key
  const isProduction =
    RAZORPAY_KEY_ID && RAZORPAY_KEY_ID.startsWith("rzp_live_");

  // Payment
  const startPayment = async () => {
    // Set payment initiated flag to prevent double clicks
    setPaymentInitiated(true);
    // Reset payment error flags for new payment attempt
    setPaymentErrorShown(false);
    paymentFailureHandledRef.current = false;
    razorpayOpenRef.current = false;

    const missingFields = [];
    if (!formData.title || !formData.title.trim()) missingFields.push("Title");
    if (!formData.firstName.trim()) missingFields.push("First Name");
    if (!formData.lastName.trim()) missingFields.push("Last Name");
    if (!formData.email.trim()) missingFields.push("Email");
    if (!formData.mobile.trim()) missingFields.push("Mobile");
    if (!formData.country.trim()) missingFields.push("Country");
    if (
      genderOptions.length > 0 &&
      (!formData.gender || !formData.gender.trim())
    )
      missingFields.push("Gender");

    if (missingFields.length > 0) {
      alert(
        `Please fill in the following required fields: ${missingFields.join(
          ", ",
        )}`,
      );
      setStep(1);
      setPaymentInitiated(false); // Reset flag on validation error
      return;
    }

    if (!validateAllMandatoryFields()) {
      alert("Please correct the highlighted contact details.");
      setStep(1);
      setPaymentInitiated(false); // Reset flag on validation error
      return;
    }

    setProcessingPayment(true);

    // MANDATORY: Ensure registration is saved successfully before proceeding to payment
    let registrationId = formData.id;
    let registrationDbId = formData.regId;

    console.log("💳 Starting payment process");
    console.log("  formData.id:", formData.id);
    console.log("  formData.regId:", formData.regId);
    console.log("  registrationId:", registrationId);
    console.log("  registrationDbId:", registrationDbId);

    if (!registrationId) {
      console.log(
        "⚠️ No registrationId found - need to save registration first",
      );

      try {
        const conferenceRegistrationData = buildConferenceRegistrationPayload({
          includeAccompanyingPersons: true,
          includeIsaMembershipNumber: true,
        });

        const regSaveResult = await saveRegistrationBeforePayment({
          conferenceRegistrationUrl: CONFERENCE_REGISTRATION_URL,
          payload: conferenceRegistrationData,
          formData,
          saveConferenceRegistration,
        });

        if (!regSaveResult.ok) {
          const regError = regSaveResult.errorMessage;
          console.error("Registration save failed:", regError);
          alert(
            `Registration failed: ${regError}. Please fix the issues and try again.`,
          );
          setProcessingPayment(false);
          setPaymentInitiated(false); // Reset flag on registration error
          setStep(1); // Go back to first step to fix issues
          return;
        }

        const savedReg = regSaveResult.savedRegistration;
        registrationId = regSaveResult.registrationId;
        registrationDbId = regSaveResult.registrationDbId;

        // Update form data with the new ID
        setFormData((prev) => ({
          ...prev,
          id: savedReg.registrationId,
          regId: savedReg.dbId || savedReg.id,
        }));

        const paymentActionMessage = isPaymentUpdate ? "updated" : "saved";
        alert(
          `Registration ${paymentActionMessage} successfully! Registration ID: ${registrationId}. Proceeding to payment...`,
        );
      } catch (regError) {
        console.error("Error saving registration before payment:", regError);
        alert(`Registration failed: ${regError.message}. Please try again.`);
        setProcessingPayment(false);
        setPaymentInitiated(false); // Reset flag on registration error
        setStep(1); // Go back to first step to fix issues
        return;
      }
    } else {
      console.log("✅ Registration already exists");
      console.log("  registrationId:", registrationId);
      console.log("  registrationDbId:", registrationDbId);
    }

    // For paid users making an additional payment, use calculateOutstandingAmounts()
    // directly — registrationFee is not loaded for them and must not be used.
    const isAdditionalPayment = isCurrentRegistrationPaid;
    const outstandingSummary = isAdditionalPayment
      ? calculateOutstandingAmounts()
      : null;
    const { currency, amountInSmallestUnit, actualAmount } =
      calculatePaymentAmountSummary({
        isAdditionalPayment,
        outstandingSummary,
        formData,
        registrationFee,
        accompanyingPersons,
        accompanyingPersonFee,
        selectedFocusedSessions,
        focusedSessionFees,
        conveyanceFeePercentage,
        shouldApplyPaperDiscount,
        paperDiscountPercentage,
        currentPaperSequence,
      });

    try {
      const breakdown = buildPaymentBreakdown({
        isAdditionalPayment,
        formData,
        registrationFee,
        hasPostPaymentCategoryOrTypeChange,
        shouldApplyPaperDiscount,
        paperDiscountPercentage,
        currentPaperSequence,
        currency,
        accompanyingPersons,
        accompanyingPersonFee,
        selectedFocusedSessions,
        focusedSessionFees,
        focusedSessions,
        conveyanceFeePercentage,
      });

      // Special handling for zero-amount orders (free items or already paid)
      if (amountInSmallestUnit === 0 || actualAmount === 0) {
        console.log("🆓 Zero amount detected - no payment needed");
        console.log("  This can happen when:");
        console.log(
          "    - User already paid base fee (formData.paid === true)",
        );
        console.log(
          "    - No new accompanying persons or focused sessions added",
        );
        console.log("    - All selected items are free (rate = 0)");

        // For zero-amount orders, we don't need to process payment
        // User should just proceed without payment or save as zero-payment record
        alert(
          "✅ No payment required. All items are either already paid or free.",
        );
        setProcessingPayment(false);
        setPaymentInitiated(false);
        return;
      }

      logPaymentBreakdownSummary({
        breakdown,
        actualAmount,
        currency,
        amountInSmallestUnit,
      });

      const orderRequestPayload = buildPaymentOrderPayload({
        amountInSmallestUnit,
        currency,
        registrationId,
        breakdown,
      });

      const orderResult = await createValidatedPaymentOrder({
        orderUrl: ORDER_URL,
        payload: orderRequestPayload,
        registrationDbId,
        createPaymentOrder,
        razorpayKeyId: RAZORPAY_KEY_ID,
      });

      if (!orderResult.ok) {
        const errorText = orderResult.errorText;
        console.error("Order creation failed:", errorText);
        alert(`Unable to create payment order: ${errorText}`);
        setProcessingPayment(false);
        setPaymentInitiated(false); // Reset flag on order creation error
        return;
      }

      const order = orderResult.order;

      await loadRazorpayScript();

      const paymentSuccessHandler = isProduction
        ? async function (response) {
            console.log(
              "🏭 PRODUCTION: Payment completed, webhook will process",
            );

            // Just show success messages and redirect
            // Webhook already processed the payment (including paper status update)
            alert("Payment successful! Processing registration...");
            setTimeout(async () => {
              alert("Registered successfully. Details Emailed");

              // If abstractStepNumber is 7, reload registration to show abstract form
              if (isPostPaymentAbstractEnabled(abstractStepNumber)) {
                console.log(
                  "📝 Abstract after payment - reloading registration to show abstract form",
                );
                try {
                  // Reload registration to get updated paid status
                  await checkExistingRegistration(
                    formData.email,
                    conferenceName,
                  );
                  setStep(POST_PAYMENT_ABSTRACT_FORM_STEP);
                } catch (error) {
                  console.error("Error reloading registration:", error);
                  // Fallback: manually update paid status and show abstract form
                  setFormData((prev) => ({ ...prev, paid: true }));
                  setStep(POST_PAYMENT_ABSTRACT_FORM_STEP);
                }
              } else {
                // Redirect to login for normal flow
                window.location.href = "/";
              }
            }, 500);
          }
        : async function (response) {
            try {
              // Clean up Razorpay instance on success
              if (razorpayInstanceRef.current) {
                razorpayInstanceRef.current = null;
              }
              razorpayOpenRef.current = false; // Reset popup flag

              // Basic validation - ensure we have all required fields
              if (
                !response.razorpay_payment_id ||
                !response.razorpay_order_id ||
                !response.razorpay_signature
              ) {
                throw new Error("Incomplete payment response from Razorpay");
              }

              // Step 1: Ensure registration is saved first to get an ID
              let registrationId = formData.id;
              let registrationDbId = formData.regId;

              if (!registrationId) {
                try {
                  const conferenceRegistrationData =
                    buildConferenceRegistrationPayload();

                  const regSaveResult =
                    await ensureRegistrationForPaymentHandler({
                      conferenceRegistrationUrl: CONFERENCE_REGISTRATION_URL,
                      payload: conferenceRegistrationData,
                      formData,
                      saveConferenceRegistration,
                    });

                  if (regSaveResult.ok) {
                    const savedReg = regSaveResult.savedRegistration;
                    registrationId = regSaveResult.registrationId;
                    registrationDbId = regSaveResult.registrationDbId;

                    // Update form data with the new ID
                    setFormData((prev) => ({
                      ...prev,
                      id: savedReg.registrationId,
                      regId: savedReg.dbId || savedReg.id,
                    }));
                  } else {
                    const regError = regSaveResult.errorMessage;
                    console.error("Failed to save registration:", regError);
                    throw new Error(`Failed to save registration: ${regError}`);
                  }
                } catch (regError) {
                  console.error(
                    "Error saving registration before payment:",
                    regError,
                  );
                  throw new Error(
                    `Could not save registration before payment: ${regError.message}`,
                  );
                }
              }

              // Step 2: Save payment entity with registration ID (Lower env only - production uses callback_url)
              try {
                await saveSuccessfulPaymentWithBreakdown({
                  paymentSaveUrl: PAYMENT_SAVE_URL,
                  order,
                  response,
                  registrationDbId,
                  amountInSmallestUnit,
                  currency,
                  savePaymentWithBreakdown,
                });
              } catch (paymentError) {
                console.warn("Error saving payment entity:", paymentError);
                // Continue anyway since we still want to update the registration with payment info
              }

              // Step 3: Confirm registration (marks as paid and confirmed)
              const confirmResult = await confirmRegistration({
                registrationBaseUrl: REGISTRATION_BASE_URL,
                registrationId,
              });

              handlePaymentConfirmationResult({
                confirmResult,
                abstractStepNumber,
                setFormData,
                setStep,
                showAlert: alert,
              });
            } catch (err) {
              console.error("Error in payment handler:", err);

              // Capture this processing error as a failed payment
              try {
                await captureFailedPayment(
                  {
                    code: "PROCESSING_ERROR",
                    description: err.message,
                    source: "payment_handler",
                    step: "payment_processing",
                    reason: "processing_failed",
                  },
                  order,
                );
              } catch (captureError) {
                console.warn(
                  "Failed to capture payment processing error:",
                  captureError,
                );
              }

              // Reset payment initiated flag so user can try again
              setPaymentInitiated(false);

              // Show user-friendly message and redirect
              alert(
                "Payment processing failed. Please try again after sometime.",
              );

              // Redirect to summary step
              setTimeout(() => {
                setStep(5);
              }, 1000);
            }
          };

      const options = buildRazorpayOptions({
        key: RAZORPAY_KEY_ID,
        order,
        currency,
        registrationDbId,
        fallbackRegistrationDbId: formData.regId,
        formData,
        handler: paymentSuccessHandler,
      });

      const rzp = new window.Razorpay(options);
      razorpayInstanceRef.current = rzp; // Store reference

      rzp.on("payment.failed", async function (response) {
        console.error("Payment failed:", response.error);

        // Use ref to prevent any duplicate handling - this is crucial
        if (paymentFailureHandledRef.current) {
          return;
        }
        paymentFailureHandledRef.current = true;
        razorpayOpenRef.current = false; // Reset popup flag

        // Close the Razorpay popup immediately and clean up
        try {
          if (razorpayInstanceRef.current) {
            razorpayInstanceRef.current.close();
            razorpayInstanceRef.current = null;
          }
        } catch (closeError) {
          console.warn("Could not close Razorpay popup:", closeError);
        }

        // Additional state-based prevention
        if (paymentErrorShown) {
          return;
        }
        setPaymentErrorShown(true);

        try {
          // Capture failed payment details
          await captureFailedPayment(response.error, order);
        } catch (captureError) {
          console.warn(
            "Failed to capture failed payment details:",
            captureError,
          );
        }

        // Reset payment initiated flag so user can try again
        setPaymentInitiated(false);

        // Show user-friendly message and redirect - use longer delay to prevent race conditions
        setTimeout(() => {
          if (paymentFailureHandledRef.current) {
            // Double check before showing alert
            alert(
              "Payment failed. Please try again after sometime or use a different payment method.",
            );

            // Redirect to summary step
            setTimeout(() => {
              setStep(5);
            }, 500);
          }
        }, 200);
      });

      // Prevent multiple popups from opening
      if (razorpayOpenRef.current) {
        setProcessingPayment(false);
        setPaymentInitiated(false);
        return;
      }

      razorpayOpenRef.current = true;
      rzp.open();
    } catch (err) {
      console.error("Payment error", err);

      // Prevent duplicate error alerts - check both ref and state
      if (!paymentFailureHandledRef.current && !paymentErrorShown) {
        paymentFailureHandledRef.current = true;
        setPaymentErrorShown(true);
        razorpayOpenRef.current = false; // Reset popup flag

        // Capture payment initialization failure
        try {
          await captureFailedPayment(
            {
              code: "INITIALIZATION_ERROR",
              description: err.message,
              source: "payment_initialization",
              step: "payment_setup",
              reason: "initialization_failed",
            },
            {
              currency: currency,
              amount: amountInSmallestUnit,
            },
          );
        } catch (captureError) {
          console.warn(
            "Failed to capture payment initialization error:",
            captureError,
          );
        }

        // Show user-friendly message and redirect
        setTimeout(() => {
          alert(
            "Payment setup failed. Please try again after sometime or check your details.",
          );

          // Redirect to step 1 to check details
          setTimeout(() => {
            setStep(1);
          }, 500);
        }, 200);
      } else {
      }

      setPaymentInitiated(false); // Reset flag on error
    } finally {
      setProcessingPayment(false);
      // Ensure popup flag is reset in finally block
      if (razorpayOpenRef.current) {
        razorpayOpenRef.current = false;
      }
    }
  };

  // Function to capture failed payment details
  const captureFailedPayment = async (error, orderData) => {
    try {
      // Ensure we have a registration ID
      let registrationId = formData.id;
      let registrationDbId = formData.regId;

      if (!registrationId) {
        try {
          const conferenceRegistrationData =
            buildConferenceRegistrationPayload();

          const regSaveResult = await ensureRegistrationForFailedPayment({
            conferenceRegistrationUrl: CONFERENCE_REGISTRATION_URL,
            payload: conferenceRegistrationData,
            formData,
            saveConferenceRegistration,
          });

          if (regSaveResult.ok) {
            const savedReg = regSaveResult.savedRegistration;
            registrationId = regSaveResult.registrationId;
            registrationDbId = regSaveResult.registrationDbId;

            setFormData((prev) => ({
              ...prev,
              id: savedReg.registrationId,
              regId: savedReg.dbId || savedReg.id,
            }));
          }
        } catch (regError) {
          console.warn(
            "Could not save registration for failed payment:",
            regError,
          );
        }
      }

      await saveFailedPayment({
        paymentSaveUrl: PAYMENT_SAVE_URL,
        error,
        orderData,
        registrationDbId,
        formData,
        savePaymentWithBreakdown,
      });
    } catch (captureError) {
      console.error("Error capturing failed payment:", captureError);
    }
  };

  // Check if registration has been saved (has ID)
  const hasRegistrationId = !!(formData.id || formData.regId);

  // Step fields
  const renderStepFields = () => {
    console.log(
      "renderStepFields - step:",
      step,
      "isAbstractRequired:",
      isAbstractRequired,
    );

    // Show paper submission summary page (step 3.x) if user just submitted paper
    if (
      step === 3 &&
      showPaperSubmissionSummary &&
      wantsToSubmitAbstract === true &&
      hasPaperSubmitted
    ) {
      return (
        <PaperSubmissionReviewStep
          formData={formData}
          paperTitle={paperTitle}
          selectedTrackName={selectedTrackName}
          presentationMode={presentationMode}
          submissionMethod={submissionMethod}
          normalizedPaperApprovalStatus={normalizedPaperApprovalStatus}
          paperApprovalStatus={paperApprovalStatus}
          isRevisionPaperStatus={isRevisionPaperStatus}
          submittedPapers={submittedPapers}
        />
      );
    }

    switch (step) {
      case 1:
        return (
          <PersonalInfoStep
            formData={formData}
            errors={errors}
            genderOptions={genderOptions}
            mobileMaxLength={MOBILE_MAX_LENGTH}
            onChange={handleChange}
          />
        );
      case 2:
        return (
          <AddressStep
            formData={formData}
            errors={errors}
            countries={countries}
            onChange={handleChange}
            onCountryChange={handleCountryChange}
          />
        );
      case 3:
        return (
          <InlineAbstractSubmissionStep
            abstractStepNumber={abstractStepNumber}
            shouldSkipInlineAbstractStep={shouldSkipInlineAbstractStep}
            hasPaperSubmitted={hasPaperSubmitted}
            wantsToSubmitAbstract={wantsToSubmitAbstract}
            onChooseAbstract={() => setWantsToSubmitAbstract(true)}
            onContinueAsDelegate={() => {
              setWantsToSubmitAbstract(false);
              setStep(4);
            }}
            isPaperLocked={isPaperLocked}
            abstractConfig={abstractConfig}
            maxAbstractChars={maxAbstractChars}
            maxFileSizeMb={maxFileSizeMb}
            paperTitle={paperTitle}
            onPaperTitleChange={setPaperTitle}
            selectedTrack={selectedTrack}
            tracks={tracks}
            onSelectedTrackChange={setSelectedTrack}
            presentationMode={presentationMode}
            onPresentationModeChange={setPresentationMode}
            submissionMethod={submissionMethod}
            onSubmissionMethodChange={setSubmissionMethod}
            abstractText={abstractText}
            onAbstractTextChange={handleInlineAbstractTextChange}
            paperFileUploaded={paperFileUploaded}
            paperFile={paperFile}
            paperFileName={paperFileName}
            paperFileError={paperFileError}
            onPaperFileChange={setPaperFile}
            onPaperFileErrorChange={setPaperFileError}
            onUploadedFileDelete={handleUploadedPaperFileDelete}
            onSelectedFileRemove={handleSelectedPaperFileRemove}
            coAuthors={coAuthors}
            coAuthorErrors={coAuthorErrors}
            onAddCoAuthor={handleAddValidatedCoAuthor}
            onUpdateCoAuthor={handleUpdateValidatedCoAuthor}
            onRemoveCoAuthor={handleRemoveValidatedCoAuthor}
            conferenceId={conferenceId}
            submittedPapers={submittedPapers}
          />
        );

      case 4:
        // Always show affiliation form (summary is shown at step 3.x after submission)
        return (
          <ProfessionalInfoStep formData={formData} onChange={handleChange} />
        );

      case 5:
      case 6:
        return (
          <>
            {/* STEP 5 - Input Fields */}
            {step === 5 && (
              <>
                <RegistrationRequirementNotice
                  categories={categories}
                  registrationTypes={registrationTypes}
                  formData={formData}
                />

                <DepartmentSelectionSection
                  formData={formData}
                  departments={departments}
                  onChange={handleChange}
                  onDepartmentChange={(e) => {
                    const selectedId = e.target.value;
                    const selectedDept = departments.find(
                      (department) => department.id === parseInt(selectedId),
                    );
                    setFormData((prev) => ({
                      ...prev,
                      departmentId: selectedId ? parseInt(selectedId) : null,
                      department: selectedDept ? selectedDept.value : "",
                    }));
                  }}
                />
                <CategoryRegistrationSelectionSection
                  formData={formData}
                  categories={categories}
                  registrationTypes={registrationTypes}
                  previousCategoriesHistory={previousCategoriesHistory}
                  previousRegistrationTypesHistory={
                    previousRegistrationTypesHistory
                  }
                  renderHistoryPills={renderHistoryPills}
                  onCategoryChange={(e) => {
                    const selectedId = e.target.value;
                    const selectedCat = categories.find(
                      (c) => c.id === parseInt(selectedId),
                    );

                    setFormData((prev) => {
                      // Save current values before switching
                      if (
                        prev.category === "ISCCM(Members)" &&
                        prev.membershipNumber
                      ) {
                        setSavedMembershipNumber(prev.membershipNumber);
                      }
                      if (
                        (prev.category === "Student" ||
                          prev.category === "student") &&
                        prev.studentIdCard
                      ) {
                        setSavedStudentIdCard(prev.studentIdCard);
                        setSavedStudentIdCardName(prev.studentIdCardName);
                        setSavedStudentIdCardType(prev.studentIdCardType);
                      }

                      // Determine values for new category
                      let membershipNumber = "";
                      let studentIdCard = null;
                      let studentIdCardName = "";
                      let studentIdCardType = "";

                      if (
                        selectedCat &&
                        selectedCat.value === "ISCCM(Members)"
                      ) {
                        // Restore saved membership number if available
                        membershipNumber =
                          savedMembershipNumber || prev.membershipNumber || "";
                      } else if (
                        selectedCat &&
                        (selectedCat.value === "Student" ||
                          selectedCat.value === "student")
                      ) {
                        // Restore saved student ID card if available
                        studentIdCard =
                          savedStudentIdCard || prev.studentIdCard || null;
                        studentIdCardName =
                          savedStudentIdCardName ||
                          prev.studentIdCardName ||
                          "";
                        studentIdCardType =
                          savedStudentIdCardType ||
                          prev.studentIdCardType ||
                          "";
                      }

                      return {
                        ...prev,
                        categoryId: selectedId ? parseInt(selectedId) : null,
                        category: selectedCat ? selectedCat.value : "",
                        membershipNumber,
                        studentIdCard,
                        studentIdCardName,
                        studentIdCardType,
                      };
                    });

                    // Clear file input and errors when not Student category or registration type
                    const isStudentSelected =
                      selectedCat &&
                      selectedCat.value.toLowerCase().includes("student");
                    const isStudentRegType =
                      formData.registrationType &&
                      formData.registrationType
                        .toLowerCase()
                        .includes("student");
                    if (!isStudentSelected && !isStudentRegType) {
                      setStudentIdCardFile(null);
                      setStudentIdCardError("");
                    }
                  }}
                  onRegistrationTypeChange={(e) => {
                    const selectedId = e.target.value;
                    const selectedType = registrationTypes.find(
                      (rt) => rt.id === parseInt(selectedId),
                    );
                    setFormData((prev) => ({
                      ...prev,
                      registrationTypeId: selectedId
                        ? parseInt(selectedId)
                        : null,
                      registrationType: selectedType ? selectedType.value : "",
                    }));

                    // Clear file input and errors when not Student registration type or category
                    const isStudentRegType =
                      selectedType &&
                      selectedType.value.toLowerCase().includes("student");
                    const isStudentCategory =
                      formData.category &&
                      formData.category.toLowerCase().includes("student");
                    if (!isStudentRegType && !isStudentCategory) {
                      setStudentIdCardFile(null);
                      setStudentIdCardError("");
                    }
                  }}
                />

                <RegistrationDocumentFieldsSection
                  formData={formData}
                  studentIdCardError={studentIdCardError}
                  onChange={handleChange}
                  onStudentIdCardUpload={handleStudentIdCardUpload}
                />

                <AccompanyingPersonsInputSection
                  formData={formData}
                  accompanyingPersonFee={accompanyingPersonFee}
                  accompanyingPersons={accompanyingPersons}
                  onNameChange={handleAccompanyingPersonNameChange}
                  onRemove={handleRemoveAccompanyingPerson}
                  onAdd={handleAddAccompanyingPerson}
                />

                <FocusedSessionsInputSection
                  formData={formData}
                  focusedSessions={focusedSessions}
                  registerForFocusedSessions={registerForFocusedSessions}
                  selectedFocusedSessions={selectedFocusedSessions}
                  focusedSessionAvailability={focusedSessionAvailability}
                  accompanyingPersonFee={accompanyingPersonFee}
                  onFocusedSessionsChange={handleFocusedSessionsChange}
                  onFocusedSessionSelect={handleFocusedSessionRadioChange}
                />

                <TrackSelectionSection
                  track_options={trackOptions}
                  checked_track_items={checkedTrackItems}
                  handle_track_change={handleTrackChange}
                  formData={formData}
                />
              </>
            )}

            {/* STEP 6 - Summary/Review */}
            {step === 6 && (
              <div className="summary-container">
                <PersonalSummarySection formData={formData} />
                <AddressSummarySection formData={formData} />
                <ProfessionalSummarySection formData={formData} />
                <RegistrationDetailsSummarySection
                  formData={formData}
                  conferenceName={conferenceName}
                  currentHost={currentHost}
                  effectiveCategoriesHistory={effectiveCategoriesHistory}
                  effectiveRegistrationTypesHistory={
                    effectiveRegistrationTypesHistory
                  }
                  registrationBaseUrl={REGISTRATION_BASE_URL}
                />

                <RegistrationFeeSummarySection
                  formData={formData}
                  feeLoading={feeLoading}
                  feeError={feeError}
                  registrationFee={registrationFee}
                  shouldApplyPaperDiscount={shouldApplyPaperDiscount}
                  paperDiscountPercentage={paperDiscountPercentage}
                  currentPaperSequence={currentPaperSequence}
                />

                <AccompanyingPersonsSummarySection
                  formData={formData}
                  accompanyingPersons={accompanyingPersons}
                  accompanyingPersonFee={accompanyingPersonFee}
                  registrationFee={registrationFee}
                />

                <PaidFocusedSessionsSummarySection
                  formData={formData}
                  selectedFocusedSessions={selectedFocusedSessions}
                  accompanyingPersonFee={accompanyingPersonFee}
                  focusedSessions={focusedSessions}
                  focusedSessionFees={focusedSessionFees}
                  registrationFee={registrationFee}
                />

                <ZeroPaymentsSummarySection
                  formData={formData}
                  accompanyingPersons={accompanyingPersons}
                  accompanyingPersonFee={accompanyingPersonFee}
                  focusedSessions={focusedSessions}
                  focusedSessionFees={focusedSessionFees}
                  selectedFocusedSessions={selectedFocusedSessions}
                />

                <TotalAmountSummarySection
                  formData={formData}
                  registrationFee={registrationFee}
                  accompanyingPersonFee={accompanyingPersonFee}
                  focusedSessionFees={focusedSessionFees}
                  conveyanceFeePercentage={conveyanceFeePercentage}
                  calculateOutstandingAmounts={calculateOutstandingAmounts}
                />

                <PaymentStatusSummarySection formData={formData} />

                <SummaryTermsAndActionsSection
                  agreedToTerms={agreedToTerms}
                  termsError={termsError}
                  onTermsAgreementChange={handleTermsAgreementChange}
                  onTermsOpen={handleTermsOpen}
                  isPaymentBlockedPendingPaperApproval={
                    isPaymentBlockedPendingPaperApproval
                  }
                  paymentBlockedPaperStatusMessage={
                    paymentBlockedPaperStatusMessage
                  }
                  onBack={() => setStep(5)}
                  totalOutstanding={
                    calculateOutstandingAmounts().totalOutstanding
                  }
                  onProceedToPayment={handleProceedToPayment}
                  saving={saving}
                  canSubmitPostPaymentAbstract={
                    canSubmitAnotherPostPaymentPaper
                  }
                  onSubmitPostPaymentAbstract={() => {
                    console.log("📝 Navigating to step 7 for paper submission");
                    handleSubmitAnotherPostPaymentPaper();
                  }}
                />
              </div>
            )}
          </>
        );
      case 7: {
        let displayCurrency = "INR";
        let displaySymbol = "₹";
        let displayAmount = "0.00";

        if (registrationFee && registrationFee.rate) {
          displayCurrency = registrationFee.currency || "INR";
          displaySymbol = formData.country === "India" ? "₹" : "₹";
        }

        const outstandingSummary = calculateOutstandingAmounts();
        const {
          registrationOutstanding,
          totalOutstanding,
          outstandingItems,
          totalOutstandingBeforeFee,
          conveyanceFeeAmount,
        } = outstandingSummary;

        const successfulPayments = getSuccessfulPayments(
          formData.payment || [],
        );
        const paidAmountFromRecords = successfulPayments.reduce(
          (sum, p) => sum + getPaymentAmount(p),
          0,
        );

        let settledPaidAmount = paidAmountFromRecords;
        if (
          settledPaidAmount === 0 &&
          formData.paid === true &&
          registrationFee
        ) {
          const baseFee = parseFloat(registrationFee.rate || 0);
          const feeRate =
            conveyanceFeePercentage !== null && conveyanceFeePercentage > 0
              ? conveyanceFeePercentage / 100
              : 0;
          settledPaidAmount = baseFee + baseFee * feeRate;
        }

        console.log("💳 Step 7 Payment Calculation (shared):", {
          registrationOutstanding,
          outstandingItems,
          totalOutstandingBeforeFee,
          conveyanceFeeAmount,
          totalOutstanding,
        });

        if (totalOutstanding > 0) {
          displayAmount = totalOutstanding.toFixed(2);
        } else if (formData.paid === true && settledPaidAmount > 0) {
          displayAmount = settledPaidAmount.toFixed(2);
        } else {
          displayAmount = totalOutstanding.toFixed(2);
        }

        return (
          <div className="summary-container">
            <div className="summary-section">
              <h4>Payment Details</h4>

              <div className="summary-row">
                <span className="summary-label">Registration Fee:</span>
                <span className="summary-value">
                  {displaySymbol}
                  {registrationFee && registrationFee.rate
                    ? parseFloat(registrationFee.rate).toFixed(2)
                    : "0.00"}{" "}
                  ({displayCurrency})
                </span>
              </div>

              {/* Show UNPAID accompanying persons breakdown only */}
              {accompanyingPersons.length > 0 &&
                accompanyingPersonFee &&
                (() => {
                  // Filter to only unpaid accompanying persons
                  const unpaidAccompanyingPersons = accompanyingPersons.filter(
                    (p) =>
                      p.feeTypeId === accompanyingPersonFee.id &&
                      !p.paid &&
                      !p.paymentId,
                  );
                  if (unpaidAccompanyingPersons.length === 0) return null;
                  return (
                    <>
                      <div className="summary-row">
                        <span className="summary-label">
                          Accompanying Persons (
                          {unpaidAccompanyingPersons.length}):
                        </span>
                        <span className="summary-value">
                          {displaySymbol}
                          {(
                            parseFloat(accompanyingPersonFee.rate) *
                            unpaidAccompanyingPersons.length
                          ).toFixed(2)}{" "}
                          ({displayCurrency})
                        </span>
                      </div>
                      <div className="summary-row">
                        <span className="summary-label"></span>
                        <span
                          className="summary-value"
                          style={{
                            fontSize: "0.9em",
                            color: "#666",
                            fontStyle: "italic",
                          }}
                        >
                          (
                          {unpaidAccompanyingPersons
                            .map((p) => p.name)
                            .join(", ")}
                          )
                        </span>
                      </div>
                    </>
                  );
                })()}

              {/* Show UNPAID items from additionalFees (saved but not paid) */}
              {(() => {
                const existingAdditionalFees = formData.additionalFees || [];

                // Get outstanding items (saved but not paid) excluding accompanying persons
                const outstandingFocusedSessions =
                  existingAdditionalFees.filter(
                    (fee) =>
                      !fee.paymentId &&
                      (!accompanyingPersonFee ||
                        fee.feeTypeId !== accompanyingPersonFee.id),
                  );

                if (outstandingFocusedSessions.length === 0) return null;

                // Calculate total and collect session details
                const sessionDetails = outstandingFocusedSessions.map((fee) => {
                  let itemRate = fee.rate ? parseFloat(fee.rate) : 0;

                  // If rate is null/0, try to get it from focusedSessionFees
                  if (itemRate === 0 && focusedSessionFees[fee.feeTypeId]) {
                    itemRate = parseFloat(
                      focusedSessionFees[fee.feeTypeId].rate || 0,
                    );
                  }

                  // Get available seats from availability data or master sessions
                  const availability =
                    focusedSessionAvailability[fee.feeTypeId];
                  const session = focusedSessions.find(
                    (s) => s.id === fee.feeTypeId,
                  );
                  const availableSeats = Number(
                    availability?.available_seats ??
                      availability?.availableSeats ??
                      session?.availableSeats ??
                      session?.available_seats ??
                      0,
                  );

                  return {
                    id: fee.id,
                    name: fee.name,
                    rate: itemRate,
                    availableSeats: availableSeats,
                  };
                });

                const outstandingTotal = sessionDetails.reduce(
                  (sum, session) => sum + session.rate,
                  0,
                );

                return (
                  <>
                    <div className="summary-row">
                      <span className="summary-label">
                        Outstanding Workshops (
                        {outstandingFocusedSessions.length}):
                      </span>
                      <span className="summary-value">
                        {displaySymbol}
                        {outstandingTotal.toFixed(2)} ({displayCurrency})
                      </span>
                    </div>
                    {sessionDetails.map((session) => (
                      <div className="summary-row" key={session.id}>
                        <span
                          className="summary-label"
                          style={{
                            paddingLeft: "20px",
                            fontSize: "0.9em",
                            color: "#666",
                          }}
                        >
                          • {session.name} ({session.availableSeats} seats
                          available)
                        </span>
                        <span
                          className="summary-value"
                          style={{ fontSize: "0.9em", color: "#666" }}
                        >
                          {displaySymbol}
                          {session.rate.toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </>
                );
              })()}

              {/* Show NEW UNPAID focused sessions from state (not yet saved) */}
              {selectedFocusedSessions.length > 0
                ? (() => {
                    const existingAdditionalFees =
                      formData.additionalFees || [];
                    const existingZeroPayments = formData.zeroPayments || [];

                    // Get IDs of sessions already in database (paid or unpaid)
                    const existingFocusedSessionIds = existingAdditionalFees
                      .filter(
                        (fee) =>
                          !accompanyingPersonFee ||
                          fee.feeTypeId !== accompanyingPersonFee.id,
                      )
                      .map((fee) => fee.feeTypeId);

                    // Filter to only NEW unpaid sessions (not in database yet)
                    const newUnpaidSessions = selectedFocusedSessions.filter(
                      (sessionId) =>
                        !existingFocusedSessionIds.includes(sessionId),
                    );

                    if (newUnpaidSessions.length === 0) return null;

                    // Calculate total and collect session details
                    const sessionDetails = newUnpaidSessions.map(
                      (sessionId) => {
                        const sessionFee = focusedSessionFees[sessionId];

                        // Try to get session name from multiple sources
                        let sessionName = null;

                        // 1. Try from focusedSessions array
                        const sessionFromList = focusedSessions.find(
                          (s) => s.id === sessionId,
                        );
                        if (sessionFromList) {
                          sessionName = sessionFromList.value;
                        }

                        // 2. Try from additionalFees (might have name stored)
                        if (!sessionName) {
                          const feeRecord = existingAdditionalFees.find(
                            (fee) => fee.feeTypeId === sessionId,
                          );
                          if (feeRecord && feeRecord.name) {
                            sessionName = feeRecord.name;
                          }
                        }

                        // 3. Try from zeroPayments
                        if (!sessionName) {
                          const zeroRecord = existingZeroPayments.find(
                            (zp) => zp.feeTypeId === sessionId,
                          );
                          if (zeroRecord && zeroRecord.name) {
                            sessionName = zeroRecord.name;
                          }
                        }

                        // 4. Fallback to session ID
                        if (!sessionName) {
                          sessionName = `Session ${sessionId}`;
                        }

                        const rate =
                          sessionFee && sessionFee.rate
                            ? parseFloat(sessionFee.rate)
                            : 0;

                        // Get available seats from availability data
                        const availability =
                          focusedSessionAvailability[sessionId];
                        const availableSeats = Number(
                          availability?.available_seats ??
                            availability?.availableSeats ??
                            sessionFromList?.availableSeats ??
                            0,
                        );

                        return {
                          id: sessionId,
                          name: sessionName,
                          rate: rate,
                          availableSeats: availableSeats,
                        };
                      },
                    );

                    const newUnpaidTotal = sessionDetails.reduce(
                      (sum, session) => sum + session.rate,
                      0,
                    );

                    return (
                      <>
                        <div className="summary-row">
                          <span className="summary-label">
                            New Workshops ({newUnpaidSessions.length}):
                          </span>
                          <span className="summary-value">
                            {displaySymbol}
                            {newUnpaidTotal.toFixed(2)} ({displayCurrency})
                          </span>
                        </div>
                        {sessionDetails.map((session) => (
                          <div className="summary-row" key={session.id}>
                            <span
                              className="summary-label"
                              style={{
                                paddingLeft: "20px",
                                fontSize: "0.9em",
                                color: "#666",
                              }}
                            >
                              • {session.name} ({session.availableSeats} seats
                              available)
                            </span>
                            <span
                              className="summary-value"
                              style={{ fontSize: "0.9em", color: "#666" }}
                            >
                              {displaySymbol}
                              {session.rate.toFixed(2)}
                            </span>
                          </div>
                        ))}
                      </>
                    );
                  })()
                : null}

              {feeError && (
                <div className="summary-row">
                  <span className="summary-value" style={{ color: "#dc2626" }}>
                    ⚠️ {feeError}
                  </span>
                </div>
              )}

              <div
                className="summary-row"
                style={{
                  marginTop: "1rem",
                  paddingTop: "1rem",
                  borderTop: "2px solid #e5e7eb",
                }}
              >
                <span
                  className="summary-label"
                  style={{ fontSize: "1.1rem", fontWeight: "600" }}
                >
                  {totalOutstanding > 0
                    ? "Total Amount:"
                    : formData.paid
                      ? "Paid Amount:"
                      : "Total Amount:"}
                </span>
                <span
                  className="summary-value"
                  style={{ fontSize: "1.1rem", fontWeight: "600" }}
                >
                  {displaySymbol}
                  {displayAmount} ({displayCurrency})
                </span>
              </div>

              {registrationFee && registrationFee.feeCategory && (
                <div className="summary-row">
                  <span className="summary-label">Fee Type:</span>
                  <span className="summary-value">
                    {registrationFee.feeCategory.value}
                  </span>
                </div>
              )}

              {registrationFee &&
                registrationFee.validFrom &&
                registrationFee.validTo && (
                  <div className="summary-row">
                    <span className="summary-label">Valid Period:</span>
                    <span className="summary-value">
                      {new Date(registrationFee.validFrom).toLocaleDateString()}{" "}
                      - {new Date(registrationFee.validTo).toLocaleDateString()}
                    </span>
                  </div>
                )}
            </div>

            <div className="summary-section">
              <h4>Registrant Information</h4>
              {formData.category && (
                <div className="summary-row">
                  <span className="summary-label">Category:</span>
                  <span className="summary-value">{formData.category}</span>
                </div>
              )}
              {formData.registrationType &&
                formData.registrationType.trim() !== "" &&
                formData.registrationType !== "TBD" && (
                  <div className="summary-row">
                    <span className="summary-label">Registration Type:</span>
                    <span className="summary-value">
                      {formData.registrationType}
                    </span>
                  </div>
                )}
              {formData.firstName && formData.lastName && (
                <div className="summary-row">
                  <span className="summary-label">Name:</span>
                  <span className="summary-value">
                    {formData.firstName} {formData.lastName}
                  </span>
                </div>
              )}
              {formData.email && (
                <div className="summary-row">
                  <span className="summary-label">Email:</span>
                  <span className="summary-value">{formData.email}</span>
                </div>
              )}
            </div>

            <div className="summary-actions">
              <div className="summary-buttons">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={startPayment}
                  disabled={
                    processingPayment ||
                    !hasRegistrationId ||
                    paymentInitiated ||
                    feeLoading ||
                    totalOutstanding <= 0
                  }
                >
                  {processingPayment
                    ? "Processing Payment..."
                    : paymentInitiated
                      ? "Payment Initiated..."
                      : feeLoading
                        ? "Calculating Fee..."
                        : totalOutstanding <= 0
                          ? "Payment Completed"
                          : !hasRegistrationId
                            ? "Save Registration First"
                            : "Pay & Complete Registration"}
                </button>
              </div>
              {!hasRegistrationId && (
                <div
                  className="registration-status warning"
                  style={{ marginTop: "1rem" }}
                >
                  ⚠️ Please go back to the Summary step and save your
                  registration before proceeding with payment.
                </div>
              )}
            </div>
          </div>
        );
      }
      case POST_PAYMENT_ABSTRACT_FORM_STEP:
        return (
          <>
            <PaymentCompletedNotice />

            {isPaperLocked && <PaperLockedNotice />}

            <AbstractSubmissionFields
              abstractConfig={abstractConfig}
              maxAbstractChars={maxAbstractChars}
              maxFileSizeMb={maxFileSizeMb}
              paperTitle={paperTitle}
              onPaperTitleChange={setPaperTitle}
              selectedTrack={selectedTrack}
              tracks={tracks}
              onSelectedTrackChange={setSelectedTrack}
              presentationMode={presentationMode}
              onPresentationModeChange={setPresentationMode}
              submissionMethod={submissionMethod}
              onSubmissionMethodChange={setSubmissionMethod}
              abstractText={abstractText}
              onAbstractTextChange={setAbstractText}
              paperFileUploaded={paperFileUploaded}
              paperFile={paperFile}
              paperFileName={paperFileName}
              paperFileError={paperFileError}
              onPaperFileChange={setPaperFile}
              onPaperFileErrorChange={setPaperFileError}
              onUploadedFileDelete={handleUploadedPaperFileDelete}
              onSelectedFileRemove={handleSelectedPaperFileRemove}
              coAuthors={coAuthors}
              onAddCoAuthor={handleAddCoAuthor}
              onUpdateCoAuthor={handleUpdateCoAuthor}
              onRemoveCoAuthor={handleRemoveCoAuthor}
              isPaperLocked={isPaperLocked}
              submittedPapers={submittedPapers}
            />
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="page-container">
      <div className="form-container">
        <div className="banner-container">
          {bannerUrl && (
            <div className="banner-container">
              <img src={bannerUrl} alt="Banner" className="banner-image" />
            </div>
          )}
        </div>
        <div className="registration-header">
          <div className="header-content">
            <h2 style={{ color: "#000", fontWeight: "bold" }}>
              Conference Registration
            </h2>
            {isExistingUser && existingUser && (
              <div className="user-info">
                <span
                  className="welcome-text"
                  style={{ color: "#000", fontWeight: "600" }}
                >
                  Welcome back,{" "}
                  <strong>
                    {existingUser.firstName} {existingUser.lastName}
                  </strong>
                  !
                </span>
                <span className="user-email">{existingUser.email}</span>
              </div>
            )}
            {!isExistingUser && prefilledEmail && (
              <div className="user-info">
                <span
                  className="new-user-text"
                  style={{ color: "#000", fontWeight: "600" }}
                >
                  Creating new registration for:{" "}
                  <strong>{prefilledEmail}</strong>
                </span>
              </div>
            )}
          </div>
          {onBackToLogin && (
            <button
              type="button"
              className="btn btn-outline back-to-login"
              onClick={onBackToLogin}
            >
              Logout
            </button>
          )}
        </div>

        <ProgressBar
          step={step}
          abstractStepNumber={abstractStepNumber}
          hidePostSummarySteps={
            step === 6 &&
            calculateOutstandingAmounts().totalOutstanding <= 0 &&
            !canSubmitAnotherPostPaymentPaper
          }
        />

        <form className="form-grid" onSubmit={(e) => e.preventDefault()}>
          {renderStepFields()}
        </form>

        {/* Button row - show for appropriate steps based on whether abstract is required */}
        {/* When abstract required: show for steps 1-5. When not required: show for steps 1,2,4,5 (skipping 3) */}
        {step >= 1 && step <= 5 && (
          <div
            className="button-row"
            style={step === 1 ? { justifyContent: "flex-end" } : {}}
          >
            {step > 1 && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={prev}
              >
                Prev
              </button>
            )}

            {step === 3 &&
            wantsToSubmitAbstract === true &&
            canShowPaperSubmitActions ? (
              <>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={async () => {
                    if (!validateCurrentStep(step)) {
                      return;
                    }
                    const success = await saveStep("draft");
                    if (success) {
                      setPaperApprovalStatus("DRAFT");
                      alert("Paper saved as draft. You can submit it later.");
                      setShowPaperSubmissionSummary(true);
                    }
                  }}
                  disabled={saving}
                  style={{ marginRight: "10px" }}
                >
                  {saving ? "Saving..." : "💾 Save as Draft"}
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={async () => {
                    if (!validateCurrentStep(step)) {
                      return;
                    }
                    const success = await saveStep("submit");
                    if (success) {
                      setPaperApprovalStatus("SUBMITTED");
                      alert(
                        "Paper submitted successfully. You will receive an email once it is reviewed.",
                      );
                      setShowPaperSubmissionSummary(true);
                    }
                  }}
                  disabled={saving}
                >
                  {saving ? "Saving..." : "📤 Submit"}
                </button>
              </>
            ) : (
              <>
                {step === 3 && showPaperSubmissionSummary ? (
                  <>
                    {!isPaymentBlockedPendingPaperApproval && (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setShowPaperSubmissionSummary(false)}
                      >
                        Back to Edit
                      </button>
                    )}
                    {isPaymentBlockedPendingPaperApproval ? null : paperApprovalStatus ===
                      "SUBMITTED" ? (
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => {
                          setShowPaperSubmissionSummary(false);
                          setStep(4);
                        }}
                      >
                        Continue to Affiliation
                      </button>
                    ) : isRevisionPaperStatus ? (
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => {
                          setShowPaperSubmissionSummary(false);
                          setStep(3);
                        }}
                      >
                        Continue to Edit Paper
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => {
                          setShowPaperSubmissionSummary(false);
                          setStep(4);
                        }}
                      >
                        Continue Registration
                      </button>
                    )}
                    {canSubmitAnotherInlinePaper && (
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => {
                          resetPaperFormForNewSubmission();
                          setWantsToSubmitAbstract(true);
                          setShowPaperSubmissionSummary(false);
                          setStep(3);
                        }}
                      >
                        Submit Another Paper
                      </button>
                    )}
                  </>
                ) : (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={saveAndContinue}
                    disabled={
                      saving ||
                      (step === 4 && isPaymentBlockedPendingPaperApproval)
                    }
                    title={
                      step === 4 && isPaymentBlockedPendingPaperApproval
                        ? "Please wait for paper approval before continuing"
                        : ""
                    }
                  >
                    {saving
                      ? "Saving..."
                      : step === 3
                        ? "Save & continue"
                        : "Save & Continue"}
                  </button>
                )}
              </>
            )}
          </div>
        )}

        {/* Post-payment abstract submission actions */}
        {step === POST_PAYMENT_ABSTRACT_FORM_STEP &&
          shouldRenderPostPaymentAbstractForm && (
            <div className="button-row">
              <button className="btn btn-secondary" onClick={() => setStep(6)}>
                Back to Summary
              </button>
              {canShowPaperSubmitActions && (
                <>
                  <button
                    className="btn btn-secondary"
                    onClick={async () => {
                      if (
                        !validateCurrentStep(POST_PAYMENT_ABSTRACT_FORM_STEP)
                      ) {
                        return;
                      }

                      const success = await saveStep("draft");
                      if (success) {
                        setResumePostPaymentAbstract(false);
                        alert("Paper saved as draft.");
                        setStep(1);
                      }
                    }}
                    disabled={saving}
                    style={{
                      padding: "15px",
                      fontSize: "16px",
                      fontWeight: "600",
                      marginRight: "10px",
                    }}
                  >
                    {saving ? "Saving..." : "💾 Save as Draft"}
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={async () => {
                      if (
                        !validateCurrentStep(POST_PAYMENT_ABSTRACT_FORM_STEP)
                      ) {
                        return;
                      }

                      const success = await saveStep("submit");
                      if (success) {
                        setResumePostPaymentAbstract(false);
                        alert("Abstract submitted successfully!");
                        setStep(1);
                      }
                    }}
                    disabled={saving}
                    style={{
                      padding: "15px",
                      fontSize: "16px",
                      fontWeight: "600",
                    }}
                  >
                    {saving ? "Saving..." : "📤 Submit"}
                  </button>
                </>
              )}
            </div>
          )}
      </div>

      {/* Terms and Conditions Modal */}
      <TermsAndConditions
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        onAcknowledge={handleTermsAcknowledged}
      />
    </div>
  );
};
export default ConferenceRegistration;
